// ============================================================
// PAYMENTS, LEDGER, RECON & BANK STATEMENT MODULE
// src/PaymentsLedger.js
// ============================================================

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createClient } from "@supabase/supabase-js";
import { fmtDMY } from "./dateUtils";
import { matchItems } from "./matchItems";
import { fuzzyMatch, parseDate as parseDateStr } from "./HistoricalImport";

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || "https://ryokwpjntufianxwdyqz.supabase.co";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ5b2t3cGpudHVmaWFueHdkeXF6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY0MDk1MTcsImV4cCI6MjA5MTk4NTUxN30.V9Bs4-Uijww6cB1jS1HBqL1RIHfRgs4WbiYP_mVmkFA";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

const fmt = (n) => "₹" + parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});

const PAYMENT_MODES = ["kotak","icici","sbi","gpay","phonepay","credit_card","cash","onetime","tds","paytm","nataraj_phonepay","kbl","sbi_old","discount","credit_note"];
const MODE_LABELS   = {
  kotak:"Kotak Bank", icici:"ICICI Bank", sbi:"SBI Bank",
  gpay:"GPay", phonepay:"Phone Pay", credit_card:"Credit Card", cash:"Cash", onetime:"One Time",
  tds:"TDS", paytm:"PayTM", nataraj_phonepay:"Nataraj Phone Pay", kbl:"Karnataka Bank", sbi_old:"SBI Bank (old a/c)",
  discount:"Discount", credit_note:"Credit Note",
};
// TDS, like cash and one-time, never appears on a bank statement — it's a
// deduction the customer takes at source, not money that moves through a
// bank. PayTM, Nataraj Phone Pay and KBL are old, discontinued accounts kept
// only so historical data can be categorized. Discount and Credit Note are
// accounting adjustments, not bank transactions — the customer's balance is
// settled without any money actually moving. None of these have a bank
// statement to reconcile against, so all get the simple "Confirm" flow.
const NO_STATEMENT_MODES = ["cash","onetime","tds","paytm","nataraj_phonepay","discount","credit_note"];
const STATEMENT_MODES = PAYMENT_MODES.filter(m => !NO_STATEMENT_MODES.includes(m));

// ── PRE-CONFIGURED BANK FORMATS ───────────────────────────────
// These are the default mappings for each bank based on actual file analysis.
// User can override via manual mapping screen if bank changes format.
const BANK_PRESETS = {
  kotak: {
    file_type:       "csv",
    skip_rows:       13,
    date_col:        "Transaction Date",
    amount_col:      "",          // split mode
    credit_col:      "Credit",
    debit_col:       "Debit",
    description_col: "Description",
    ref_col:         "Chq / Ref No.",
    balance_col:     "Balance",
    balance_sign_col: "Dr / Cr",  // balance is unsigned; "DR" means overdrawn
    type_col:        "",
    date_format:     "DD-MM-YYYY",
    credit_keyword:  "",
    strip_time:      true,        // date has HH:MM:SS suffix
    filter_col:      "",
    filter_val:      "",
    notes: "CSV · Skip 13 header rows · Split Credit/Debit columns · Strip time from date",
    // Newer Kotak export: Withdrawal/Deposit headings, DD/MM/YY dates with time.
    alts: [{
      file_type: "csv", skip_rows: 10,
      date_col: "Date", amount_col: "", credit_col: "Deposit", debit_col: "Withdrawal",
      description_col: "Description", ref_col: "Chq / Ref number", balance_col: "Balance", type_col: "",
      date_format: "DD/MM/YYYY", credit_keyword: "", strip_time: true, filter_col: "", filter_val: "",
    }],
  },
  icici: {
    file_type:       "xlsx",
    skip_rows:       16,
    date_col:        "Transaction Date",
    amount_col:      "",
    credit_col:      "Deposit Amt (INR)",
    debit_col:       "Withdrawal Amt (INR)",
    description_col: "Transaction Remarks",
    ref_col:         "Cheque. No./Ref. No.",
    type_col:        "",
    date_format:     "DD MMM YYYY",
    credit_keyword:  "",
    strip_time:      false,
    filter_col:      "",
    filter_val:      "",
    notes: "XLSX · Skip 16 header rows · Split Deposit/Withdrawal columns · Date format: 02/Mar/2026"
  },
  sbi: {
  file_type:       "tab",
  skip_rows:       19,
  date_col:        "Txn Date",
  amount_col:      "",
  credit_col:      "Credit",
  debit_col:       "Debit",
  description_col: "Description",
  ref_col:         "Ref No./Cheque No.",
  type_col:        "",
  date_format:     "DD/MM/YYYY",
  credit_keyword:  "",
  strip_time:      false,
  filter_col:      "",
  filter_val:      "",
  notes: "XLS (text/tab) · Skip 19 header rows · Split Credit/Debit · DD/MM/YYYY dates"
},
  // Karnataka Bank overdraft account — .xls export, newest entry first,
  // account details above the table, dates DD/MM/YYYY.
  kbl: {
    file_type:       "xlsx",
    skip_rows:       15,
    date_col:        "Date",
    amount_col:      "",
    credit_col:      "Credit",
    debit_col:       "Debit",
    description_col: "Description",
    ref_col:         "Cheque No",
    balance_col:     "Balance (INR)",
    type_col:        "",
    date_format:     "DD/MM/YYYY",
    credit_keyword:  "",
    strip_time:      false,
    filter_col:      "",
    filter_val:      "",
    notes: "XLS · Skip 15 header rows · Split Debit/Credit · DD/MM/YYYY dates · Newest first",
    // Later exports (from mid-2024) use different headings and DD-MM-YYYY dates.
    alts: [{
      file_type: "xlsx", skip_rows: 15,
      date_col: "Tran Date", amount_col: "", credit_col: "Credit (INR)", debit_col: "Debit (INR)",
      description_col: "Narration", ref_col: "Cheque/RefNum", balance_col: "Balance (INR)", type_col: "",
      date_format: "DD-MM-YYYY", credit_keyword: "", strip_time: false, filter_col: "", filter_val: "",
    }],
  },
  // Older SBI current account — .xls export, oldest first, dates M/D/YY.
  sbi_old: {
    file_type:       "xlsx",
    skip_rows:       18,
    date_col:        "Txn Date",
    amount_col:      "",
    credit_col:      "Credit",
    debit_col:       "Debit",
    description_col: "Description",
    ref_col:         "Ref No./Cheque No.",
    balance_col:     "Balance",
    type_col:        "",
    date_format:     "MM/DD/YYYY",
    credit_keyword:  "",
    strip_time:      false,
    filter_col:      "",
    filter_val:      "",
    notes: "XLS · Skip 18 header rows · Split Debit/Credit · M/D/YY dates · Oldest first"
  },
  gpay: {
    file_type:       "csv",
    skip_rows:       0,
    date_col:        "Creation time",
    amount_col:      "Amount",
    credit_col:      "",
    debit_col:       "",
    description_col: "Payer/Receiver",
    ref_col:         "Transaction ID",
    type_col:        "",
    date_format:     "YYYY-MM-DD",
    credit_keyword:  "",
    strip_time:      true,
    filter_col:      "Status",    // only import Settled
    filter_val:      "Settled",
    notes: "CSV · No header rows to skip · All entries are credits · Filter Status=Settled · Strip time from date"
  },
  phonepay: {
    file_type:       "csv",
    skip_rows:       0,
    date_col:        "Transaction Date",
    amount_col:      "Total Transaction Amount",
    credit_col:      "",
    debit_col:       "",
    description_col: "Store Name",
    ref_col:         "PhonePe Reference Id",
    type_col:        "",
    date_format:     "YYYY-MM-DD",
    credit_keyword:  "",
    strip_time:      true,
    filter_col:      "Transaction Status",
    filter_val:      "COMPLETED",
    notes: "CSV · No header rows to skip · All entries are credits · Filter Status=COMPLETED · Strip time from date"
  },
  credit_card: {
    file_type:       "xlsx",
    skip_rows:       0,
    date_col:        "PAID Date",
    amount_col:      "Txn Amt",
    credit_col:      "",
    debit_col:       "",
    description_col: "Card Number",
    ref_col:         "RRN No.",
    type_col:        "Txn Type",
    date_format:     "EXCEL",     // Excel datetime object
    credit_keyword:  "SALE",      // SALE=credit, REFUND=debit
    strip_time:      false,
    filter_col:      "",
    filter_val:      "",
    notes: "XLSX · No header rows to skip · SALE=credit, REFUND=debit · Date is Excel datetime"
  },
};

// ── DATE PARSERS ──────────────────────────────────────────────
const DATE_FORMATS = [
  "DD/MM/YYYY","DD-MM-YYYY","MM/DD/YYYY","YYYY-MM-DD",
  "DD MMM YYYY","DD/MMM/YYYY","D/M/YYYY","EXCEL"
];

const MONTHS = {jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12};

function parseDate(str, fmt) {
  if (!str && str !== 0) return null;
  // Excel serial date number
  if (fmt === "EXCEL" || typeof str === "number") {
    const d = new Date(Math.round((str - 25569) * 86400 * 1000));
    return isNaN(d) ? null : d;
  }
  if (typeof str !== "string") str = String(str);
  str = str.trim().split(" ")[0].split("T")[0]; // strip time
  try {
    if (fmt === "DD/MM/YYYY" || fmt === "D/M/YYYY") {
      const [d,m,y0] = str.split(/[/-]/);
      if (!y0) return null;
      const y = y0.length === 2 ? `20${y0}` : y0;   // "31/01/25" → 2025
      return new Date(`${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`);
    }
    if (fmt === "DD-MM-YYYY") {
      const [d,m,y] = str.split("-");
      if (!y) return null;
      return new Date(`${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`);
    }
    if (fmt === "MM/DD/YYYY") {
      const [m,d,y0] = str.split("/");
      if (!y0) return null;
      const y = y0.length === 2 ? `20${y0}` : y0;   // "4/30/24" → 2024
      return new Date(`${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`);
    }
    if (fmt === "YYYY-MM-DD") {
      return new Date(str);
    }
    if (fmt === "DD MMM YYYY") {
      // handles "02/Mar/2026" or "02 Mar 2026"
      const parts = str.split(/[\s/-]/);
      if (parts.length >= 3) {
        const d = String(parts[0]).padStart(2,"0");
        const m = String(MONTHS[parts[1].toLowerCase().slice(0,3)]||1).padStart(2,"0");
        const y = parts[2];
        return new Date(`${y}-${m}-${d}`);
      }
      return new Date(str);
    }
    if (fmt === "DD/MMM/YYYY") {
      const [d,mon,y] = str.split("/");
      const m = String(MONTHS[mon.toLowerCase().slice(0,3)]||1).padStart(2,"0");
      return new Date(`${y}-${m}-${String(d).padStart(2,"0")}`);
    }
    return new Date(str);
  } catch { return null; }
}

function toISODate(d) {
  if (!d || isNaN(d.getTime())) return null;
  return d.toISOString().slice(0,10);
}

// ── FILE PARSERS ──────────────────────────────────────────────
function parseSplitLine(line, delim) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQuotes = !inQuotes; }
    else if (ch === delim && !inQuotes) { result.push(current.trim()); current = ""; }
    else { current += ch; }
  }
  result.push(current.trim());
  return result;
}

function parseCSVText(text, skipRows=0, delim=",") {
  const allLines = text.split(/\r?\n/);
  // Auto-detect delimiter if not specified
  if (delim === "auto") {
    const sample = allLines[skipRows] || allLines[0] || "";
    const counts = {",":0,"\t":0,";":0,"|":0};
    for (const d of Object.keys(counts)) counts[d] = sample.split(d).length;
    delim = Object.keys(counts).reduce((a,b) => counts[a]>counts[b]?a:b);
  }
  // Auto-detect header row if skipRows not specified
  let headerIdx = skipRows;
  if (skipRows === 0) {
    for (let i=0; i<Math.min(allLines.length,5); i++) {
      const parts = parseSplitLine(allLines[i], delim).filter(p=>p.replace(/"/g,"").trim());
      if (parts.length >= 4) { headerIdx = i; break; }
    }
  }
  const lines = allLines.slice(headerIdx).filter(l => l.trim());
  if (lines.length < 2) return { headers:[], rows:[] };
  const headers = parseSplitLine(lines[0], delim).map(h => h.replace(/^"|"$/g,"").trim());
  const rows = lines.slice(1).map(line => {
    const vals = parseSplitLine(line, delim);
    const obj = {};
    headers.forEach((h,i) => { obj[h] = (vals[i]||"").replace(/^"|"$/g,"").trim(); });
    return obj;
  }).filter(r => Object.values(r).some(v => v && v !== ""));
  return { headers, rows };
}

// Parse XLSX using SheetJS (loaded via CDN in browser)
async function parseXLSX(file, skipRows=0) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const XLSX = window.XLSX;
        if (!XLSX) { reject(new Error("SheetJS not loaded")); return; }
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type:"array", cellDates:true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const allRows = XLSX.utils.sheet_to_json(ws, { header:1, raw:false, dateNF:"DD/MM/YYYY" });
        // Find header row
        let headerIdx = skipRows;
        for (let i=skipRows; i<Math.min(allRows.length, skipRows+5); i++) {
          const row = allRows[i] || [];
          if (row.filter(c=>c!=null&&c!=="").length >= 4) { headerIdx=i; break; }
        }
        // Some banks leave blank spacer columns between the headings; keep each
        // heading tied to its real column position instead of compacting them.
        const headerCells = (allRows[headerIdx]||[]).map(h => String(h||"").trim());
        const headers = headerCells.filter(h=>h);
        const rows = allRows.slice(headerIdx+1).map(row => {
          const obj = {};
          headerCells.forEach((h,i) => { if (h) obj[h] = row[i] != null ? String(row[i]).trim() : ""; });
          return obj;
        }).filter(r => Object.values(r).some(v => v && v !== ""));
        resolve({ headers, rows });
      } catch(err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Checks the statement's running balance still adds up row by row. A missing
// page, a doubled row or a mis-read amount shows up as a break in the chain.
// Works for oldest-first and newest-first files.
function checkBalanceChain(rows) {
  const withBal = rows.filter(r => r.bal != null);
  if (withBal.length < 2) return null;
  const descending = withBal[0].isoDate > withBal[withBal.length-1].isoDate;
  let broken = 0, checked = 0;
  const delta = (r) => r.entryType === "credit" ? r.amount : -r.amount;
  for (let i = 0; i < withBal.length; i++) {
    const prev = descending ? withBal[i+1] : withBal[i-1];
    if (!prev) continue;
    checked++;
    if (Math.abs(prev.bal + delta(withBal[i]) - withBal[i].bal) > 0.02) broken++;
  }
  const first = descending ? withBal[withBal.length-1] : withBal[0];
  const last  = descending ? withBal[0] : withBal[withBal.length-1];
  return {
    checked, broken,
    opening: Math.round((first.bal - delta(first)) * 100) / 100,
    closing: last.bal,
  };
}

// Process rows using bank preset/mapping
function processRows(rows, preset) {
  const results = [];
  for (const row of rows) {
    // Apply filter if specified (e.g. GPay Status=Settled)
    if (preset.filter_col && preset.filter_val) {
      const val = (row[preset.filter_col]||"").trim();
      if (val !== preset.filter_val) continue;
    }

    // Get date
    let rawDate = row[preset.date_col] || "";
    if (preset.strip_time) rawDate = rawDate.split(" ")[0].split("T")[0];
    const parsedDate = parseDate(rawDate, preset.date_format);
    const isoDate = toISODate(parsedDate);
    if (!isoDate) continue;

    // Get amount and type
    let amount = 0;
    let entryType = "credit";

    if (preset.credit_col && preset.debit_col) {
      // Split credit/debit columns (Kotak, ICICI, SBI)
      const cVal = parseFloat((row[preset.credit_col]||"").replace(/[,₹\s]/g,"")) || 0;
      const dVal = parseFloat((row[preset.debit_col]||"").replace(/[,₹\s]/g,""))  || 0;
      if (cVal > 0) { amount = cVal; entryType = "credit"; }
      else if (dVal > 0) { amount = dVal; entryType = "debit"; }
      else continue;
    } else {
      // Single amount column
      amount = parseFloat((row[preset.amount_col]||"").replace(/[,₹\s]/g,"")) || 0;
      if (amount === 0) continue;
      amount = Math.abs(amount);
      if (preset.type_col && preset.credit_keyword) {
        const typeVal = (row[preset.type_col]||"").toUpperCase();
        entryType = typeVal.includes(preset.credit_keyword.toUpperCase()) ? "credit" : "debit";
      } else {
        entryType = "credit"; // GPay/PhonePay are all credits
      }
    }

    const desc = preset.description_col ? (row[preset.description_col]||"") : "";
    // Banks vary the spacing in this heading ("Chq / Ref No." vs "Chq /Ref No."), so match loosely.
    const refKey = preset.ref_col && (preset.ref_col in row ? preset.ref_col
      : Object.keys(row).find(k => k.replace(/\s+/g,"").toLowerCase() === preset.ref_col.replace(/\s+/g,"").toLowerCase()));
    const ref  = refKey ? (row[refKey]||"") : "";

    const balRaw = preset.balance_col ? parseFloat(String(row[preset.balance_col]||"").replace(/[,₹\s]/g,"")) : NaN;
    const balSigned = preset.balance_sign_col && /^d/i.test(String(row[preset.balance_sign_col]||"").trim()) ? -balRaw : balRaw;
    results.push({ isoDate, amount, entryType, desc, ref, bal: isNaN(balSigned) ? null : balSigned, valid:true });
  }
  return results;
}

// ── SHARED STYLES ─────────────────────────────────────────────
const S = {
  pageHeader: {display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:28,paddingBottom:20,borderBottom:"1px solid var(--paper3)"},
  pageTitle:  {fontFamily:"var(--serif)",fontSize:29,fontWeight:300,letterSpacing:"-0.5px"},
  subtitle:   {fontSize:12,color:"var(--ink3)",marginTop:6},
  btnPrimary: {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"none",background:"var(--accent)",color:"#fff",fontWeight:500},
  btnGhost:   {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)"},
  btnSm:      {padding:"5px 10px",fontSize:12,borderRadius:8,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)",fontFamily:"var(--mono)"},
  card:       {background:"#fff",borderRadius:10,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",overflow:"hidden"},
  statCard:   (c) => ({background:"#fff",borderRadius:10,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)",borderLeft:`3px solid ${c}`}),
  th:         (r) => ({padding:"10px 16px",textAlign:r?"right":"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,whiteSpace:"nowrap"}),
  td:         {padding:"12px 16px",fontSize:13,color:"var(--ink)"},
  input:      {padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,color:"var(--ink)",background:"var(--paper)",outline:"none",width:"100%"},
  label:      {fontSize:11,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500},
  overlay:    {position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20},
};

// ── ADD PAYMENT MODAL ─────────────────────────────────────────
function AddPaymentModal({ onClose, onSave, preselectedCustomer, record }) {
  const isEdit = !!record;
  // The payment row already carries customer_id — combine it with the
  // joined row's display fields instead of needing the list query to
  // select the customer's id too.
  const recordCustomer = record ? { id: record.customer_id, name: record.customers?.name||"", type: record.customers?.type } : null;
  const [customers, setCustomers] = useState([]);
  const [customerQ, setCustomerQ] = useState(recordCustomer?.name||preselectedCustomer?.name||"");
  const [customer, setCustomer]   = useState(recordCustomer||preselectedCustomer||null);
  const [amount, setAmount]       = useState(record?.amount!=null?String(record.amount):"");
  const [mode, setMode]           = useState(record?.payment_mode||"cash");
  const [date, setDate]           = useState(record?.payment_date||new Date().toISOString().slice(0,10));
  const [refNo, setRefNo]         = useState(record?.reference_no||"");
  const [notes, setNotes]         = useState(record?.notes||"");
  const [saving, setSaving]       = useState(false);
  const [custOpen, setCustOpen]   = useState(false);

  useEffect(() => {
const loadAllCusts = async () => {
  let all = [];
  let from = 0;
  while (true) {
    const { data, error } = await supabase.from("customers")
      .select("id,name,type,phone,gstin,address_line1,address_line2,city,state,pincode,country")
      .eq("is_active", true).order("name")
      .range(from, from + 999);
    if (error || !data || data.length === 0) break;
    all = [...all, ...data];
    if (data.length < 1000) break;
    from += 1000;
  }
  setCustomers(all);
};
loadAllCusts();
  }, []);

  const filteredCusts = matchItems(customers, customerQ, c=>c.name, 50).list;

  const save = async () => {
    if (!customer?.id) return alert("Please select a customer.");
    if (!amount || parseFloat(amount)<=0) return alert("Please enter a valid amount.");
    // A bank-confirmed payment is matched to a specific statement entry —
    // changing who it belongs to or how much it was breaks that match, so
    // release it first (same as changing its mode).
    const changedConfirmed = isEdit && record.bank_confirmed &&
      (customer.id!==record.customer_id || parseFloat(amount)!==parseFloat(record.amount));
    if (changedConfirmed && !window.confirm("This payment is bank-confirmed against a statement entry.\n\nChanging the customer or amount will unconfirm it and release that match. Continue?")) return;
    setSaving(true);
    try {
      const payload = {
        customer_id:customer.id, amount:parseFloat(amount),
        payment_mode:mode, payment_date:date,
        reference_no:refNo||null, notes:notes||null,
      };
      if (isEdit) {
        if (changedConfirmed) {
          const {error:e1} = await supabase.from("bank_statement_entries").update({matched_payment_id:null}).eq("matched_payment_id",record.id);
          if (e1) throw e1;
          Object.assign(payload,{bank_confirmed:false,bank_ref:null,confirmed_at:null});
        }
        const {data,error} = await supabase.from("customer_payments").update(payload).eq("id",record.id).select();
        if (error) throw error;
        if (!data || data.length===0) throw new Error("This payment no longer exists — refresh the page.");
      } else {
        const {error} = await supabase.from("customer_payments").insert({...payload, bank_confirmed:false});
        if (error) throw error;
      }
      onSave();
    } catch(e) { alert("Error: "+e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:480}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={S.pageTitle}>{isEdit?"Edit payment":"Record payment"}</div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"20px 28px 28px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            <div style={{gridColumn:"span 2"}}>
              <label style={{...S.label,display:"block",marginBottom:5}}>Customer *</label>
              <div style={{position:"relative"}}>
                <input value={customerQ||""} onChange={e=>{setCustomerQ(e.target.value);setCustOpen(true);if(!e.target.value)setCustomer(null);}}
                  onFocus={()=>setCustOpen(true)} placeholder="Type to search customers…" style={S.input}/>
                {custOpen&&filteredCusts.length>0&&(
                  <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid var(--paper3)",borderRadius:8,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",zIndex:50,maxHeight:200,overflowY:"auto",marginTop:2}}>
                    {filteredCusts.map(c=>(
                      <div key={c.id} style={{padding:"9px 12px",fontSize:13,cursor:"pointer",borderBottom:"1px solid var(--paper2)"}}
                        onMouseDown={()=>{setCustomer(c);setCustomerQ(c.name);setCustOpen(false);}}>
                        <div style={{fontWeight:500}}>{c.name}</div>
                        <div style={{fontSize:11,color:"var(--ink3)"}}>{c.type}{c.phone?" · "+c.phone:""}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Amount (₹) *</label>
              <input type="number" min="0" step="0.01" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00" style={S.input}/>
            </div>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Payment mode</label>
              <select value={mode} onChange={e=>setMode(e.target.value)} style={S.input}>
                {PAYMENT_MODES.map(m=><option key={m} value={m}>{MODE_LABELS[m]}</option>)}
              </select>
            </div>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Payment date</label>
              <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={S.input}/>
            </div>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Reference no.</label>
              <input value={refNo} onChange={e=>setRefNo(e.target.value)} placeholder="Cheque / UTR / Txn ID" style={S.input}/>
            </div>
            <div style={{gridColumn:"span 2"}}>
              <label style={{...S.label,display:"block",marginBottom:5}}>Notes</label>
              <input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Optional notes" style={S.input}/>
            </div>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:24,paddingTop:20,borderTop:"1px solid var(--paper2)"}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving?"Saving…":isEdit?"💾 Save changes":"💳 Record payment"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── BANK STATEMENT UPLOAD MODAL ───────────────────────────────
function StatementUploadModal({ onClose, onSave, toast }) {
  const [step, setStep]               = useState(1); // 1=select bank, 2=upload, 3=manual map, 4=preview
  const [mode, setMode]               = useState("");
  const [preset, setPreset]           = useState(null);
  const [savedMapping, setSavedMapping] = useState(null);
  const [manualMap, setManualMap]     = useState({});
  const [headers, setHeaders]         = useState([]);
  const [preview, setPreview]         = useState([]);
  const [rawRows, setRawRows]         = useState([]);
  const [parsing, setParsing]         = useState(false);
  const [dupWarning, setDupWarning]   = useState(null);
  const [saving, setSaving]           = useState(false);
  const fileRef = useRef(null);
  const balCheck = useMemo(() => checkBalanceChain(preview), [preview]);

  const selectBank = async (m) => {
    setMode(m);
    const p = BANK_PRESETS[m];
    setPreset(p);
    // Check if user has a saved custom mapping
    const {data} = await supabase.from("bank_statement_mappings").select("*").eq("payment_mode",m).single();
    setSavedMapping(data||null);
    setStep(2);
  };

  const handleFile = async (file) => {
    if (!file || !preset) return;
    setParsing(true);
    try {
      // A bank can change its export layout over the years, so try each known
      // layout until one's column headings match the file.
      const layoutFits = (l, h) => h.includes(l.date_col) && (!l.credit_col || h.includes(l.credit_col));
      const parseWith = async (l) => {
        if (l.file_type === "xlsx") {
          // Load SheetJS dynamically if not present
          if (!window.XLSX) {
            await new Promise((res,rej) => {
              const s = document.createElement("script");
              s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
              s.onload=res; s.onerror=rej;
              document.head.appendChild(s);
            });
          }
          return parseXLSX(file, l.skip_rows);
        }
        // CSV or tab-delimited text
        const text = await file.text();
        return parseCSVText(text, l.skip_rows, l.file_type === "tab" ? "\t" : "auto");
      };
      let parsed = null, fitted = null;
      for (const l of [preset, ...(preset.alts||[])]) {
        const attempt = await parseWith(l);
        if (!parsed) parsed = attempt;
        if (layoutFits(l, attempt.headers)) { parsed = attempt; fitted = l; break; }
      }
      setHeaders(parsed.headers);
      setRawRows(parsed.rows);

      // Use saved mapping if available and columns match, else use preset
      const activePreset = savedMapping && parsed.headers.includes(savedMapping.date_col) ? savedMapping : (fitted || preset);

      // Check if preset columns exist in parsed headers
      const dateExists   = parsed.headers.includes(activePreset.date_col);
      const amountExists = activePreset.amount_col ? parsed.headers.includes(activePreset.amount_col) : true;
      const creditExists = activePreset.credit_col ? parsed.headers.includes(activePreset.credit_col) : true;

      if (dateExists && amountExists && creditExists) {
        // Auto-apply preset — go straight to preview
        const rows = processRows(parsed.rows, activePreset);
        setPreview(rows);
        setStep(4);
      } else {
        // Columns don't match — show manual mapping
        setManualMap({
          date_col:        activePreset.date_col||"",
          amount_col:      activePreset.amount_col||"",
          credit_col:      activePreset.credit_col||"",
          debit_col:       activePreset.debit_col||"",
          description_col: activePreset.description_col||"",
          ref_col:         activePreset.ref_col||"",
          type_col:        activePreset.type_col||"",
          date_format:     activePreset.date_format||"DD/MM/YYYY",
          credit_keyword:  activePreset.credit_keyword||"",
          filter_col:      activePreset.filter_col||"",
          filter_val:      activePreset.filter_val||"",
          strip_time:      activePreset.strip_time||false,
        });
        setStep(3);
      }
    } catch(e) {
      alert("Error reading file: "+e.message);
    } finally { setParsing(false); }
  };

  const applyManualMap = () => {
    if (!manualMap.date_col) return alert("Please select a Date column.");
    if (!manualMap.amount_col && !manualMap.credit_col) return alert("Please select an Amount or Credit column.");
    const rows = processRows(rawRows, manualMap);
    if (rows.length === 0) return alert("No valid rows found. Check column selections and date format.");
    setPreview(rows);
    setStep(4);
  };

  // Entries already saved for this account over the file's date range. Rows in
  // the file that match one (same date, amount and type) are skipped, so
  // overlapping statements can be uploaded without double-counting.
  const loadExisting = async () => {
    const dates = preview.map(r=>r.isoDate).filter(Boolean);
    const minDate = dates.reduce((a,b)=>a<b?a:b);
    const maxDate = dates.reduce((a,b)=>a>b?a:b);
    const all = [];
    for (let from = 0; ; from += 1000) {
      const {data} = await supabase.from("bank_statement_entries")
        .select("entry_date,amount,entry_type").eq("payment_mode",mode)
        .gte("entry_date",minDate).lte("entry_date",maxDate).order("id").range(from, from+999);
      if (!data) break;
      all.push(...data);
      if (data.length < 1000) break;
    }
    return { all };
  };
  const entryKey = (d,a,t) => `${d}|${Number(a).toFixed(2)}|${t}`;

  const saveStatement = async () => {
    setSaving(true);
    try {
      let toInsert = preview, skipped = 0;
      if (preview.length > 0) {
        const { all } = await loadExisting();
        const left = {};
        all.forEach(e => { const k = entryKey(e.entry_date,e.amount,e.entry_type); left[k] = (left[k]||0)+1; });
        toInsert = preview.filter(r => {
          const k = entryKey(r.isoDate,r.amount,r.entryType);
          if (left[k] > 0) { left[k]--; skipped++; return false; }
          return true;
        });
      }
      // Save custom mapping to DB if user manually mapped
      if (step===3||manualMap.date_col) {
        const mapToSave = step===3 ? manualMap : preset;
        await supabase.from("bank_statement_mappings").upsert({
          payment_mode:mapToSave.payment_mode||mode,
          date_col:mapToSave.date_col, amount_col:mapToSave.amount_col||"",
          description_col:mapToSave.description_col||null, type_col:mapToSave.type_col||null,
          ref_col:mapToSave.ref_col||null, date_format:mapToSave.date_format,
          credit_keyword:mapToSave.credit_keyword||null,
          credit_col:mapToSave.credit_col||null, debit_col:mapToSave.debit_col||null,
          updated_at:new Date().toISOString(),
        },{onConflict:"payment_mode"});
      }
      const entries = toInsert.map(r=>({
        payment_mode:mode, entry_date:r.isoDate,
        amount:r.amount, description:r.desc||null,
        entry_type:r.entryType, raw_ref:r.ref||null,
      }));
      if (entries.length) {
        const {error} = await supabase.from("bank_statement_entries").insert(entries);
        if (error) throw error;
      }
      toast(`${entries.length} entries uploaded for ${MODE_LABELS[mode]}${skipped?` (${skipped} already there, skipped)`:""}!`,"success");
      onSave();
    } catch(e) { alert("Error: "+e.message); }
    finally { setSaving(false); }
  };

  const sel = (k,v) => setManualMap(m=>({...m,[k]:v}));

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:720,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"flex-start",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",zIndex:10,paddingBottom:12,borderBottom:"1px solid var(--paper2)"}}>
          <div>
            <div style={S.pageTitle}>Upload bank statement</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>
              {["","1. Select bank","2. Upload file","3. Map columns","4. Review & save"][step]}
            </div>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)",marginTop:4}} onClick={onClose}>✕</button>
        </div>

        <div style={{padding:"20px 28px 28px"}}>

          {/* Step 1 — Select bank */}
          {step===1&&(
            <div>
              <div style={{fontSize:13,color:"var(--ink2)",marginBottom:16}}>Select which bank / payment mode to upload:</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                {STATEMENT_MODES.map(m=>(
                  <div key={m} onClick={()=>selectBank(m)}
                    style={{padding:"16px",border:"1px solid var(--paper3)",borderRadius:10,cursor:"pointer",background:"#fff",transition:"all 0.15s"}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--accent2)";e.currentTarget.style.background="var(--accent-l)";}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--paper3)";e.currentTarget.style.background="#fff";}}>
                    <div style={{fontFamily:"var(--mono)",fontSize:14,fontWeight:500,marginBottom:4}}>{MODE_LABELS[m]}</div>
                    <div style={{fontSize:11,color:"var(--ink3)"}}>{BANK_PRESETS[m].notes}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2 — Upload file */}
          {step===2&&(
            <div>
              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--info-l)",borderRadius:8,fontSize:13,color:"var(--info)"}}>
                <strong>{MODE_LABELS[mode]}</strong> · {preset?.notes}
                {savedMapping && <div style={{marginTop:4,color:"var(--accent)",fontSize:12}}>✓ Custom column mapping saved — will auto-apply if columns match</div>}
              </div>

              {parsing && <div style={{textAlign:"center",padding:30,color:"var(--ink3)"}}>Reading file…</div>}

              {!parsing && (
                <div style={{border:"2px dashed var(--paper3)",borderRadius:10,padding:48,textAlign:"center",cursor:"pointer",background:"var(--paper)",transition:"all 0.15s"}}
                  onClick={()=>fileRef.current?.click()}
                  onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor="var(--accent2)";e.currentTarget.style.background="var(--accent-l)";}}
                  onDragLeave={e=>{e.currentTarget.style.borderColor="var(--paper3)";e.currentTarget.style.background="var(--paper)";}}>
                  <onDrop onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor="var(--paper3)";handleFile(e.dataTransfer.files[0]);}}/>
                  <div style={{fontSize:37,marginBottom:8}}>📂</div>
                  <div style={{fontSize:14,fontWeight:500,color:"var(--ink2)"}}>
                    Click to select {preset?.file_type==="xlsx"?"Excel (.xlsx)":preset?.file_type==="tab"?"Excel/Text (.xls)":"CSV (.csv)"} file
                  </div>
                  <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>or drag and drop here</div>
                  <input ref={fileRef} type="file"
                    accept={preset?.file_type==="xlsx"?".xlsx,.xls":preset?.file_type==="tab"?".xls,.xlsx,.txt":".csv,.txt"}
                    style={{display:"none"}}
                    onChange={e=>handleFile(e.target.files[0])}/>
                </div>
              )}

              <div style={{marginTop:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <button style={S.btnSm} onClick={()=>setStep(1)}>← Back</button>
              </div>
            </div>
          )}

          {/* Step 3 — Manual column mapping */}
          {step===3&&(
            <div>
              <div style={{marginBottom:14,padding:"10px 14px",background:"var(--warn-l)",borderRadius:8,fontSize:13,color:"var(--warn)"}}>
                ⚠ Column mismatch — please re-map columns for {MODE_LABELS[mode]}. Once saved this will be remembered.
              </div>
              <div style={{marginBottom:14,padding:"10px 14px",background:"var(--paper)",borderRadius:8,fontSize:12,color:"var(--ink3)"}}>
                Detected columns: {headers.join(" · ")}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                {[
                  {k:"date_col",        label:"Date column *"},
                  {k:"date_format",     label:"Date format *", type:"select", options:DATE_FORMATS},
                  {k:"amount_col",      label:"Amount column (if single)"},
                  {k:"credit_col",      label:"Credit amount column (if split)"},
                  {k:"debit_col",       label:"Debit amount column (if split)"},
                  {k:"description_col", label:"Description column"},
                  {k:"ref_col",         label:"Reference / UTR column"},
                  {k:"type_col",        label:"Txn type column (CR/DR)"},
                  {k:"credit_keyword",  label:"Credit keyword in type col", type:"text"},
                  {k:"filter_col",      label:"Filter column (e.g. Status)"},
                  {k:"filter_val",      label:"Filter value (e.g. Settled)", type:"text"},
                ].map(f=>(
                  <div key={f.k}>
                    <label style={{...S.label,display:"block",marginBottom:5}}>{f.label}</label>
                    {f.type==="text" ? (
                      <input value={manualMap[f.k]||""} onChange={e=>sel(f.k,e.target.value)} style={S.input}/>
                    ) : f.type==="select" ? (
                      <select value={manualMap[f.k]||""} onChange={e=>sel(f.k,e.target.value)} style={S.input}>
                        {(f.options||[]).map(o=><option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <select value={manualMap[f.k]||""} onChange={e=>sel(f.k,e.target.value)} style={S.input}>
                        <option value="">— Not used —</option>
                        {headers.map(h=><option key={h} value={h}>{h}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"space-between",marginTop:20,paddingTop:16,borderTop:"1px solid var(--paper2)"}}>
                <button style={S.btnSm} onClick={()=>setStep(2)}>← Back</button>
                <button style={S.btnPrimary} onClick={applyManualMap}>Preview entries →</button>
              </div>
            </div>
          )}

          {/* Step 4 — Preview & upload */}
          {step===4&&(
            <div>
              <div style={{marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{fontSize:13,color:"var(--ink2)"}}>
                  <strong style={{color:"var(--accent)"}}>{preview.length} entries</strong> ready to upload for <strong>{MODE_LABELS[mode]}</strong>
                  <span style={{marginLeft:8,fontSize:12,color:"var(--ink3)"}}>
                    ({preview.filter(r=>r.entryType==="credit").length} credits · {preview.filter(r=>r.entryType==="debit").length} debits)
                  </span>
                </div>
                <button style={S.btnSm} onClick={()=>setStep(3)}>← Change column mapping</button>
              </div>

              {balCheck&&(
                <div style={{marginBottom:12,padding:"10px 14px",borderRadius:8,fontSize:12,
                  background:balCheck.broken?"var(--warn-l)":"var(--accent-l)",color:balCheck.broken?"var(--warn)":"var(--accent)"}}>
                  {balCheck.broken
                    ? `⚠ Running balance breaks at ${balCheck.broken} of ${balCheck.checked} rows — a row may be missing, doubled or misread. Check before uploading.`
                    : `✓ Running balance checks out across all ${balCheck.checked} rows.`}
                  <span style={{marginLeft:8,color:"var(--ink3)"}}>Opening {fmt(balCheck.opening)} → Closing {fmt(balCheck.closing)}</span>
                </div>
              )}

              {dupWarning&&(
                <div style={{marginBottom:12,padding:"12px 16px",background:"var(--warn-l)",border:"1px solid var(--warn)",borderRadius:8,fontSize:13,color:"var(--warn)"}}>
                  ⚠ {dupWarning}
                  <div style={{marginTop:10,display:"flex",gap:8}}>
                    <button style={S.btnPrimary} onClick={()=>{setDupWarning(null);saveStatement();}}>Upload anyway</button>
                    <button style={S.btnSm} onClick={()=>setDupWarning(null)}>Cancel</button>
                  </div>
                </div>
              )}

              <div style={{maxHeight:380,overflowY:"auto",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:16}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0,zIndex:1}}>
                    <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                      <th style={S.th()}>Date</th>
                      <th style={S.th()}>Description</th>
                      <th style={S.th()}>Type</th>
                      <th style={S.th(true)}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0,200).map((r,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--paper2)",background:r.entryType==="credit"?"#f9fff9":"#fff8f8"}}>
                        <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{r.isoDate}</td>
                        <td style={{...S.td,fontSize:12,maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={r.desc}>{r.desc||"—"}</td>
                        <td style={S.td}>
                          <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:r.entryType==="credit"?"var(--accent-l)":"var(--danger-l)",color:r.entryType==="credit"?"var(--accent)":"var(--danger)"}}>
                            {r.entryType}
                          </span>
                        </td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500}}>{fmt(r.amount)}</td>
                      </tr>
                    ))}
                    {preview.length>200&&<tr><td colSpan={4} style={{padding:12,textAlign:"center",fontSize:12,color:"var(--ink3)"}}>Showing first 200 of {preview.length} entries</td></tr>}
                  </tbody>
                </table>
              </div>

              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button style={S.btnGhost} onClick={onClose}>Cancel</button>
                <button style={S.btnPrimary} onClick={()=>saveStatement(false)} disabled={saving||!!dupWarning}>
                  {saving?"Uploading…":`⬆ Upload ${preview.length} entries`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MATCH STATEMENT MODAL ─────────────────────────────────────
function MatchStatementModal({ payment, onClose, onMatched, toast }) {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch]     = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const {data} = await supabase.from("bank_statement_entries")
        .select("*").eq("payment_mode",payment.payment_mode)
        .is("matched_payment_id",null)
        .order("entry_date",{ascending:false});
      setEntries(data||[]);
      setLoading(false);
      if (data) {
        const match = data.find(e =>
          parseFloat(e.amount)===parseFloat(payment.amount) && e.entry_date===payment.payment_date
        );
        if (match) setSelected(match.id);
      }
    };
    load();
  }, [payment]);

  const isAutoMatch = (e) =>
    parseFloat(e.amount)===parseFloat(payment.amount) && e.entry_date===payment.payment_date;

  // Auto-matched entry first, then anything with the same amount on a
  // different date, then everything else — so the entry you're looking
  // for is usually right at the top instead of buried in a long,
  // date-only-sorted list.
  const matchRank = (e) => isAutoMatch(e) ? 0 : (parseFloat(e.amount)===parseFloat(payment.amount) ? 1 : 2);
  const filtered = entries.filter(e => {
    const s = search.toLowerCase();
    return (e.description||"").toLowerCase().includes(s)||
      (e.raw_ref||"").toLowerCase().includes(s)||
      (e.entry_date||"").includes(s)||
      String(e.amount).includes(s);
  }).sort((a,b) => matchRank(a)-matchRank(b));

const confirmMatch = async () => {
  if (!selected) return alert("Please select a matching entry.");
  const entry = entries.find(e=>e.id===selected);
  if (!entry) return;

  // Amount must match exactly
  const statementAmt = parseFloat(entry.amount)||0;
  const paymentAmt   = parseFloat(payment.amount)||0;
  if (statementAmt !== paymentAmt) {
    alert(
      `Amount mismatch — cannot confirm.\n\n` +
      `Recorded payment: ${fmt(paymentAmt)}\n` +
      `Statement entry:  ${fmt(statementAmt)}\n\n` +
      `Please select a statement entry with the exact same amount.`
    );
    return;
  }

  await supabase.from("customer_payments").update({
    bank_confirmed:true, bank_ref:entry.raw_ref||entry.description||entry.entry_date,
    confirmed_at:new Date().toISOString(),
  }).eq("id",payment.id);
  await supabase.from("bank_statement_entries").update({matched_payment_id:payment.id}).eq("id",selected);
  toast("Payment matched and confirmed!","success");
  onMatched();
};

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:680,maxHeight:"90vh",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"24px 28px 16px",flexShrink:0,borderBottom:"1px solid var(--paper2)"}}>
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
            <div style={S.pageTitle}>Match to bank statement</div>
            <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
          </div>
          <div style={{marginTop:12,padding:"10px 14px",background:"var(--info-l)",borderRadius:8,display:"flex",gap:20,fontSize:13}}>
            {[["Payment",fmt(payment.amount)],["Date",fmtDMY(payment.payment_date)],["Customer",payment.customers?.name],["Mode",MODE_LABELS[payment.payment_mode]]].map(([lbl,val])=>(
              <div key={lbl}><span style={{color:"var(--ink3)",fontSize:11,textTransform:"uppercase",letterSpacing:"0.5px",display:"block"}}>{lbl}</span><strong>{val}</strong></div>
            ))}
          </div>
          <div style={{marginTop:10,position:"relative"}}>
            <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--ink3)",fontSize:14,pointerEvents:"none"}}>🔍</span>
            <input placeholder="Search by amount, date, description…" value={search} onChange={e=>setSearch(e.target.value)}
              style={{...S.input,paddingLeft:34,background:"#fff"}}/>
          </div>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"12px 28px"}}>
          {loading&&<div style={{textAlign:"center",padding:30,color:"var(--ink3)"}}>Loading statement entries…</div>}
          {!loading&&filtered.length===0&&(
            <div style={{textAlign:"center",padding:40,color:"var(--ink3)"}}>
              <div style={{fontSize:29,marginBottom:8,opacity:0.5}}>📄</div>
              <p style={{fontSize:13}}>No unmatched entries for {MODE_LABELS[payment.payment_mode]}.</p>
              <p style={{fontSize:12,marginTop:4,color:"var(--ink3)"}}>Upload a statement first from the Payments page.</p>
            </div>
          )}
          {!loading&&filtered.map((e,i)=>{
            const am = isAutoMatch(e);
            const isSel = selected===e.id;
            // Divider right where the ranking shifts from "same amount" to
            // "everything else" — only meaningful with no search narrowing
            // things down already.
            const prevRank = i>0 ? matchRank(filtered[i-1]) : -1;
            const showDivider = !search && matchRank(e)===2 && prevRank!==2;
            return (
              <div key={e.id}>
              {showDivider&&<div style={{fontSize:11,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)",margin:"14px 0 6px",paddingTop:i>0?10:0,borderTop:i>0?"1px solid var(--paper2)":"none"}}>Other entries</div>}
              <div onClick={()=>setSelected(e.id)}
                style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",marginBottom:6,border:`2px solid ${isSel?"var(--accent)":am?"var(--accent2)":"var(--paper3)"}`,borderRadius:10,cursor:"pointer",background:isSel?"var(--accent-l)":am?"#f0faf4":"#fff",transition:"all 0.15s"}}>
                <input type="radio" checked={isSel} onChange={()=>setSelected(e.id)} style={{accentColor:"var(--accent)",flexShrink:0}}/>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontFamily:"var(--mono)",fontWeight:500,fontSize:14}}>{fmt(e.amount)}</span>
                    <span style={{fontFamily:"var(--mono)",fontSize:12,color:"var(--ink3)"}}>{fmtDMY(e.entry_date)}</span>
                    <span style={{display:"inline-block",padding:"1px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:e.entry_type==="credit"?"var(--accent-l)":"var(--danger-l)",color:e.entry_type==="credit"?"var(--accent)":"var(--danger)"}}>{e.entry_type}</span>
                    {am&&<span style={{display:"inline-block",padding:"1px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:"#fff9c4",color:"#f57f17"}}>★ Auto-matched</span>}
                  </div>
                  {e.description&&<div style={{fontSize:12,color:"var(--ink3)",marginTop:2}}>{e.description}</div>}
                  {e.raw_ref&&<div style={{fontSize:11,color:"var(--ink3)",fontFamily:"var(--mono)",marginTop:1}}>{e.raw_ref}</div>}
                </div>
              </div>
              </div>
            );
          })}
        </div>

        <div style={{padding:"16px 28px",flexShrink:0,borderTop:"1px solid var(--paper2)",display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button style={S.btnGhost} onClick={onClose}>Cancel</button>
          <button style={{
  ...S.btnPrimary,
  ...(selected && entries.find(e=>e.id===selected) && parseFloat(entries.find(e=>e.id===selected)?.amount) !== parseFloat(payment.amount)
    ? {background:"var(--danger)",cursor:"not-allowed",opacity:0.7}
    : {})
}} onClick={confirmMatch} disabled={!selected}>
  {selected && entries.find(e=>e.id===selected) && parseFloat(entries.find(e=>e.id===selected)?.amount) !== parseFloat(payment.amount)
    ? "⚠ Amount mismatch — cannot confirm"
    : "✓ Confirm match"}
</button>
        </div>
      </div>
    </div>
  );
}

// ── DAILY RECEIVABLE UPLOAD (Date/Customer/Amount/Bank → customer_payments) ─
const BANK_TO_MODE = {
  gpay:"gpay", kotak:"kotak", icici:"icici", sbi:"sbi", "sbi new":"sbi",
  "phone pay":"phonepay", phonepay:"phonepay", "credit card":"credit_card", cash:"cash",
  onetime:"onetime", "one time":"onetime", "one-time":"onetime",
  tds:"tds", "t.d.s":"tds", "tax deduction at source":"tds", "tax deducted at source":"tds",
  paytm:"paytm", "pay tm":"paytm",
  // Old, discontinued accounts kept only for historical data — checked
  // before the generic "phone pay"/"phonepay" entries above via the
  // longest-key-first matching in matchBankMode, so this stays its own
  // account rather than being folded into the current PhonePe one.
  "nataraj phone pay":"nataraj_phonepay", "nataraj phonepay":"nataraj_phonepay",
  "nataraj":"nataraj_phonepay",
  kbl:"kbl", "karnataka bank":"kbl", "ktb":"kbl",
  "sbi old":"sbi_old", "old sbi":"sbi_old",
  discount:"discount",
  "credit note":"credit_note", "creditnote":"credit_note", "credit_note":"credit_note",
};
const normalizeBank = (s) => (s||"").toLowerCase().trim().replace(/\s+/g," ");

// Matches a raw bank/mode string from a file against a known payment mode,
// tolerating the extra words banks and customers tend to add — "ICICI Bank",
// "SBI New", "Natraj Phone Pay" — instead of requiring an exact match, which
// previously left rows like "ICICI Bank" unmatched even though "icici" is
// a known mode.
const matchBankMode = (raw) => {
  const n = normalizeBank(raw).replace(/\bbanks?\b/g," ").replace(/\s+/g," ").trim();
  if (!n) return null;
  if (BANK_TO_MODE[n]) return BANK_TO_MODE[n];
  // Longest key first, so e.g. "credit card" is checked before any shorter
  // key that might accidentally be a substring of it.
  const key = Object.keys(BANK_TO_MODE).sort((a,b)=>b.length-a.length).find(k=>n.includes(k));
  return key ? BANK_TO_MODE[key] : null;
};

// Reads date cells as actual Date objects (raw:true) instead of a
// SheetJS-formatted string — dateNF is not reliably honored for every cell
// format, which previously caused DD/MM vs MM/DD ambiguity (e.g. "8/1/26").
// Reading the real Date object sidesteps that entirely.
async function parseDailyReceivablesXLSX(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const XLSX = window.XLSX;
        if (!XLSX) { reject(new Error("SheetJS not loaded")); return; }
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type:"array", cellDates:true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const allRows = XLSX.utils.sheet_to_json(ws, { header:1, raw:true, defval:"" });
        let headerIdx = 0;
        for (let i=0;i<Math.min(allRows.length,5);i++) {
          const row = allRows[i]||[];
          if (row.filter(c=>c!=null&&c!=="").length>=4) { headerIdx=i; break; }
        }
        const headers = (allRows[headerIdx]||[]).map(h=>String(h||"").trim()).filter(h=>h);
        const rows = allRows.slice(headerIdx+1).map(row => {
          const obj = {};
          headers.forEach((h,i) => { obj[h] = row[i]; });
          return obj;
        }).filter(r => Object.values(r).some(v => v!=null && v!==""));
        resolve({ headers, rows });
      } catch(err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function excelValueToISODate(v) {
  if (v instanceof Date) {
    // Excel date serials from some source files land a few seconds short
    // of the intended midnight (float drift in whatever generated the
    // file — confirmed against a real sample: 23:59:50 the day before,
    // not a clean midnight), which SheetJS preserves exactly. These cells
    // are always date-only, so if the time-of-day is within a few minutes
    // of local midnight, snap to that boundary. Must compare against LOCAL
    // midnight, not round to a UTC hour grid — timezones like IST (+5:30)
    // sit on a half-hour offset, so UTC-hour rounding never lands on local
    // midnight and silently keeps the wrong day.
    let y=v.getFullYear(), m=v.getMonth(), d=v.getDate();
    const msIntoDay = v.getTime() - new Date(y,m,d).getTime();
    if (msIntoDay > 86400000 - 300000) {
      const next = new Date(y,m,d+1);
      y=next.getFullYear(); m=next.getMonth(); d=next.getDate();
    }
    return `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  }
  return parseDateStr(String(v||""));
}

async function loadAllCustomersForUpload() {
  let all = []; let from = 0;
  while (true) {
    const { data, error } = await supabase.from("customers")
      .select("id,name,phone").eq("is_active",true).order("name")
      .range(from, from+999);
    if (error || !data || data.length===0) break;
    all = [...all, ...data];
    if (data.length<1000) break;
    from += 1000;
  }
  return all;
}

async function fetchExistingPayments(minDate, maxDate) {
  let all = []; let from = 0;
  while (true) {
    const { data, error } = await supabase.from("customer_payments")
      .select("id,payment_date,customer_id,amount,payment_mode")
      .gte("payment_date",minDate).lte("payment_date",maxDate)
      .range(from, from+999);
    if (error) throw error;
    if (!data || data.length===0) break;
    all = [...all, ...data];
    if (data.length<1000) break;
    from += 1000;
  }
  return all;
}

// Count-based dedup — same proven algorithm as TempLedger.js's receivables
// upload, adapted to customer_payments's columns. Conflicts (same
// date/customer/mode, different amount) default to "skip"; the user must
// explicitly toggle "import anyway" per row — nothing is auto-imported.
function classifyPaymentRows(rows, existingRows) {
  const existingExactCount = {};
  const existingByDCM = {};
  existingRows.forEach(e => {
    const exact = `${e.payment_date}|${e.customer_id}|${e.amount}|${e.payment_mode}`;
    existingExactCount[exact] = (existingExactCount[exact]||0)+1;
    const dcm = `${e.payment_date}|${e.customer_id}|${e.payment_mode}`;
    (existingByDCM[dcm] = existingByDCM[dcm]||[]).push({ id:e.id, amount:parseFloat(e.amount) });
  });
  rows.forEach(r => { r.status = undefined; r.conflictAmounts = undefined; });
  const groups = {};
  rows.filter(r => r.customerId && r.mode).forEach(r => {
    const exact = `${r.isoDate}|${r.customerId}|${r.amount}|${r.mode}`;
    (groups[exact] = groups[exact]||[]).push(r);
  });
  Object.entries(groups).forEach(([exact,groupRows]) => {
    const existingCount = existingExactCount[exact]||0;
    const toInsert = Math.max(0, groupRows.length-existingCount);
    const parts = exact.split("|");
    const mode = parts[parts.length-1];
    const amtStr = parts[parts.length-2];
    const cust = parts[parts.length-3];
    const d = parts.slice(0,parts.length-3).join("|");
    const dcm = `${d}|${cust}|${mode}`;
    const differingExisting = (existingByDCM[dcm]||[]).filter(e => e.amount !== parseFloat(amtStr));
    groupRows.forEach((r,i) => {
      if (i >= groupRows.length-toInsert) {
        if (differingExisting.length>0) {
          r.status = "conflict";
          r.conflictAmounts = differingExisting.map(e=>e.amount);
          if (r.importAnyway===undefined) r.importAnyway = false;
        } else {
          r.status = "ready";
        }
      } else {
        r.status = "duplicate";
      }
    });
  });
  rows.forEach(r => {
    if (!r.status) r.status = !r.customerId ? "unmatched" : !r.mode ? "unmatched-bank" : "ready";
  });
  return rows;
}

const PAY_STATUS_BADGE = {
  ready:            { bg:"var(--accent-l)", color:"var(--accent)", label:"✓ Ready" },
  duplicate:        { bg:"var(--paper2)",   color:"var(--ink3)",   label:"Already exists" },
  conflict:         { bg:"var(--danger-l)", color:"var(--danger)", label:"⚠ Conflict" },
  unmatched:        { bg:"var(--warn-l)",   color:"var(--warn)",   label:"No customer match" },
  "unmatched-bank":{ bg:"var(--warn-l)",   color:"var(--warn)",   label:"Unknown bank" },
};

// ── QUICK-ADD CUSTOMER (opened from an unresolved-name row during upload) ──
function QuickAddCustomerModal({ prefillName, onClose, onCreated, toast }) {
  const [name, setName]   = useState(prefillName||"");
  const [type, setType]   = useState("counter");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast("Customer name is required.","error");
    setSaving(true);
    try {
      const { data, error } = await supabase.from("customers")
        .insert({ name:name.trim(), type, phone:phone||null, is_active:true })
        .select().single();
      if (error) throw error;
      toast("Customer created.");
      onCreated(data);
    } catch(e) { toast("Error: "+e.message,"error"); }
    finally { setSaving(false); }
  };

  return (
    <div style={{...S.overlay, zIndex:400}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:420}}>
        <div style={{padding:"20px 24px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={S.pageTitle}>New customer</div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"16px 24px 24px"}}>
          <label style={{...S.label,display:"block",marginBottom:5}}>Customer name *</label>
          <input value={name} onChange={e=>setName(e.target.value)} autoFocus style={{...S.input,marginBottom:14}}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:18}}>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Type</label>
              <select value={type} onChange={e=>setType(e.target.value)} style={S.input}>
                <option value="counter">Counter</option>
                <option value="outstation">Outstation</option>
                <option value="tpf">TPF</option>
                <option value="prv">PRV</option>
              </select>
            </div>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Phone</label>
              <input value={phone} onChange={e=>setPhone(e.target.value)} style={S.input}/>
            </div>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving?"Saving…":"💾 Create & use"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DailyReceivableUploadModal({ onClose, onDone, toast }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [fileName, setFileName] = useState("");
  const [rawRows, setRawRows] = useState([]);
  const [existingRows, setExistingRows] = useState([]);
  const [parsedRows, setParsedRows] = useState([]);
  const [result, setResult] = useState(null);
  const [pickerQuery, setPickerQuery] = useState({});
  const [addCustomerFor, setAddCustomerFor] = useState(null); // {name, indices} while quick-add modal is open
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true); setFileName(file.name);
    try {
      let parsed;
      if (file.name.endsWith(".xlsx")||file.name.endsWith(".xls")) {
        if (!window.XLSX) {
          await new Promise((res,rej) => {
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
            s.onload=res; s.onerror=rej; document.head.appendChild(s);
          });
        }
        parsed = await parseDailyReceivablesXLSX(file);
      } else {
        const text = await file.text();
        parsed = parseCSVText(text, 0, "auto");
      }
      const { headers, rows } = parsed;
      const findCol = (pred) => headers.find(h => pred(h.toLowerCase().trim()));
      const dateCol = findCol(h=>h==="date");
      const custCol = findCol(h=>h==="customer"||h==="name");
      const amountCol = findCol(h=>h==="amount");
      const bankCol = findCol(h=>h==="bank"||h==="method"||h==="mode");
      const num = (v) => { const n = parseFloat(String(v||"0").replace(/[,₹\s]/g,"")); return isNaN(n)?0:n; };

      const custs = await loadAllCustomersForUpload();
      setCustomers(custs);

      const parsedRowsRaw = rows.map(row => {
        const isoDate = excelValueToISODate(dateCol?row[dateCol]:"");
        const rawName = String((custCol?row[custCol]:"")||"").trim();
        const amount = num(amountCol?row[amountCol]:0);
        const bankRaw = String((bankCol?row[bankCol]:"")||"").trim();
        const mode = matchBankMode(bankRaw);
        const match = fuzzyMatch(rawName, custs);
        return {
          isoDate, rawName, amount, bankRaw, mode,
          customerId: match?match.id:null,
          customerName: match?match.name:null,
          valid: !!(isoDate && rawName && amount>0 && bankRaw),
        };
      }).filter(r => r.isoDate || r.rawName);

      const validRows = parsedRowsRaw.filter(r=>r.valid);
      if (!validRows.length) { toast("No valid rows found — check the file has Date/Customer/Amount/Bank columns.","error"); setBusy(false); return; }

      const resolvableDates = validRows.filter(r=>r.customerId&&r.mode).map(r=>r.isoDate).sort();
      const minDate = resolvableDates[0], maxDate = resolvableDates[resolvableDates.length-1];
      const existing = minDate ? await fetchExistingPayments(minDate,maxDate) : [];

      setRawRows(validRows);
      setExistingRows(existing);
      setParsedRows(classifyPaymentRows(validRows, existing));
      setStep(2);
    } catch(e) {
      toast("Failed to read file: "+e.message,"error");
    } finally { setBusy(false); }
  };

  const reclassify = (updatedRows) => {
    setRawRows(updatedRows);
    setParsedRows(classifyPaymentRows(updatedRows, existingRows));
  };

  const resolveGroupCustomer = (indices, cust) => {
    const idxSet = new Set(indices);
    const updated = rawRows.map((r,i)=> idxSet.has(i) ? {...r, customerId:cust.id, customerName:cust.name} : r);
    reclassify(updated);
  };

  const handleCustomerCreated = (indices, cust) => {
    setCustomers(cs => [...cs, cust]);
    resolveGroupCustomer(indices, cust);
    setAddCustomerFor(null);
  };

  const resolveBank = (idx, mode) => {
    const updated = rawRows.map((r,i)=> i===idx ? {...r, mode} : r);
    reclassify(updated);
  };

  const toggleImportAnyway = (idx) => {
    const updated = rawRows.map((r,i)=> i===idx ? {...r, importAnyway: !r.importAnyway} : r);
    reclassify(updated);
  };

  const readyRows = parsedRows.filter(r=>r.status==="ready");
  const conflictRows = parsedRows.filter(r=>r.status==="conflict");
  const conflictImporting = conflictRows.filter(r=>r.importAnyway);
  const duplicateCount = parsedRows.filter(r=>r.status==="duplicate").length;
  const needsResolution = parsedRows.filter(r=>r.status==="unmatched"||r.status==="unmatched-bank").length;

  // Deduped unresolved-customer groups — one entry per unique raw name
  // (not one per row), so a name repeated across dozens of rows only needs
  // resolving once.
  const unresolvedNameGroups = (() => {
    const map = new Map();
    parsedRows.forEach((r,idx) => {
      if (r.status!=="unmatched") return;
      if (!map.has(r.rawName)) map.set(r.rawName, { name:r.rawName, count:0, indices:[] });
      const g = map.get(r.rawName);
      g.count++; g.indices.push(idx);
    });
    return [...map.values()];
  })();

  const toImport = [...readyRows, ...conflictImporting];
  // Unresolved rows are simply left out of this import, not a hard block —
  // create the missing customer/bank mapping and re-upload the same file
  // later; count-based dedup means already-imported rows won't duplicate.
  const canImport = toImport.length>0;

  const doImport = async () => {
    setBusy(true);
    try {
      const chunks = [];
      for (let i=0;i<toImport.length;i+=300) chunks.push(toImport.slice(i,i+300));
      for (const chunk of chunks) {
        const { error } = await supabase.from("customer_payments").insert(chunk.map(r => ({
          customer_id: r.customerId, amount: r.amount, payment_mode: r.mode,
          payment_date: r.isoDate, reference_no: null,
          notes: `Daily upload — ${fileName}`, bank_confirmed: false,
        })));
        if (error) throw error;
      }
      setResult({ inserted: toImport.length, duplicates: duplicateCount, skipped: conflictRows.length-conflictImporting.length, unresolved: needsResolution });
      setStep(3);
      onDone && onDone();
    } catch(e) { toast("Import failed: "+e.message,"error"); }
    finally { setBusy(false); }
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:960,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"flex-start",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",zIndex:10,paddingBottom:12,borderBottom:"1px solid var(--paper2)"}}>
          <div>
            <div style={S.pageTitle}>Upload daily receivables</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>{["","Upload file","Preview & resolve","Done"][step]}</div>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"20px 28px 28px"}}>
          {step===1 && (
            <div>
              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--paper)",borderRadius:8,fontSize:13,color:"var(--ink3)"}}>
                Expected columns: <strong>Date · Customer · Amount · Bank</strong>. Already-recorded payments are detected automatically by date + customer + amount + bank — genuine repeat same-day payments still import correctly.
              </div>
              <div style={{border:"2px dashed var(--paper3)",borderRadius:10,padding:48,textAlign:"center",cursor:"pointer",background:"var(--paper)"}}
                onClick={()=>fileRef.current?.click()}>
                <div style={{fontSize:37,marginBottom:8}}>📂</div>
                <div style={{fontSize:14,fontWeight:500,color:"var(--ink2)"}}>{busy?"Reading file…":"Click to select Excel or CSV file"}</div>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
              </div>
            </div>
          )}
          {step===2 && (
            <div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
                {[
                  {label:"Ready to import", value:toImport.length, color:"var(--accent)"},
                  {label:"Already exists",  value:duplicateCount,  color:"var(--ink3)"},
                  {label:"Conflicts",       value:conflictRows.length, color:"var(--danger)"},
                  {label:"Needs resolution",value:needsResolution, color:"var(--warn)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300,lineHeight:1}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>
              {conflictRows.length>0 && (
                <div style={{marginBottom:12,padding:"10px 14px",background:"var(--danger-l)",borderRadius:8,fontSize:12,color:"var(--danger)"}}>
                  {conflictRows.length} row{conflictRows.length!==1?"s":""} have the same date/customer/bank already on record with a different amount. They're skipped by default — toggle "Import anyway" per row if it's a genuine separate payment.
                </div>
              )}
              {unresolvedNameGroups.length>0 && (
                <div style={{marginBottom:12,padding:"14px 16px",background:"var(--warn-l)",borderRadius:8}}>
                  <div style={{fontSize:13,fontWeight:500,color:"var(--warn)",marginBottom:10}}>
                    ⚠ {unresolvedNameGroups.length} customer name{unresolvedNameGroups.length!==1?"s":""} ({needsResolution} row{needsResolution!==1?"s":""}) couldn't be matched. Resolve each name once here — every row with that name updates together.
                  </div>
                  {unresolvedNameGroups.map(g => (
                    <div key={g.name} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderTop:"1px solid rgba(0,0,0,0.06)"}}>
                      <div style={{flex:"0 0 220px",fontSize:13,fontWeight:500}}>{g.name} <span style={{color:"var(--ink3)",fontWeight:400}}>({g.count} row{g.count!==1?"s":""})</span></div>
                      <div style={{flex:1,maxWidth:280,position:"relative"}}>
                        <input value={pickerQuery[g.name]||""} onChange={e=>setPickerQuery(p=>({...p,[g.name]:e.target.value}))}
                          placeholder="Search customer…" style={{...S.input,fontSize:12}}/>
                        {(pickerQuery[g.name]||"")!=="" && (
                          <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid var(--paper3)",borderRadius:8,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",zIndex:50,maxHeight:180,overflowY:"auto",marginTop:2}}>
                            {matchItems(customers, pickerQuery[g.name], c=>c.name, 50).list.map(c=>(
                              <div key={c.id} style={{padding:"7px 10px",fontSize:12,cursor:"pointer",borderBottom:"1px solid var(--paper2)"}}
                                onMouseDown={()=>{resolveGroupCustomer(g.indices,c);setPickerQuery(p=>({...p,[g.name]:""}));}}>{c.name}</div>
                            ))}
                          </div>
                        )}
                      </div>
                      <button style={S.btnSm} onClick={()=>setAddCustomerFor(g)}>+ Add customer</button>
                    </div>
                  ))}
                </div>
              )}
              {parsedRows.some(r=>r.status==="unmatched-bank") && (
                <div style={{marginBottom:12,padding:"10px 14px",background:"var(--warn-l)",borderRadius:8,fontSize:12,color:"var(--warn)"}}>
                  Some rows have an unrecognized bank name — pick a mode for them in the table below.
                </div>
              )}
              <div style={{maxHeight:420,overflowY:"auto",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:16}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0,zIndex:1}}>
                    <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                      <th style={S.th()}>Date</th><th style={S.th()}>Customer</th><th style={S.th()}>Bank</th>
                      <th style={S.th(true)}>Amount</th><th style={S.th()}>Status</th><th style={S.th()}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map((r,idx)=>{
                      const badge = PAY_STATUS_BADGE[r.status]||PAY_STATUS_BADGE.unmatched;
                      return (
                        <tr key={idx} style={{borderBottom:"1px solid var(--paper2)"}}>
                          <td style={S.td}>{fmtDMY(r.isoDate)}</td>
                          <td style={{...S.td,fontSize:12}}>
                            {r.status==="unmatched"
                              ? <span style={{color:"var(--warn)"}}>{r.rawName} <span style={{color:"var(--ink3)"}}>(resolve above)</span></span>
                              : (r.customerName||r.rawName)}
                          </td>
                          <td style={{...S.td,fontSize:12}}>
                            {r.status==="unmatched-bank" ? (
                              <select value={r.mode||""} onChange={e=>resolveBank(idx,e.target.value)} style={{...S.input,fontSize:12}}>
                                <option value="">— Pick mode — ({r.bankRaw})</option>
                                {PAYMENT_MODES.map(m=><option key={m} value={m}>{MODE_LABELS[m]}</option>)}
                              </select>
                            ) : (MODE_LABELS[r.mode]||r.bankRaw)}
                          </td>
                          <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12}}>{fmt(r.amount)}</td>
                          <td style={S.td}>
                            <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:badge.bg,color:badge.color}}>{badge.label}</span>
                            {r.status==="conflict" && (
                              <div style={{fontSize:11,color:"var(--ink3)",marginTop:2}}>on record: {r.conflictAmounts.map(a=>fmt(a)).join(", ")}</div>
                            )}
                          </td>
                          <td style={S.td}>
                            {r.status==="conflict" && (
                              <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11,cursor:"pointer",whiteSpace:"nowrap"}}>
                                <input type="checkbox" checked={!!r.importAnyway} onChange={()=>toggleImportAnyway(idx)} style={{width:12,height:12,cursor:"pointer"}}/>
                                Import anyway
                              </label>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
                <button style={S.btnGhost} onClick={()=>setStep(1)}>← Back</button>
                <button style={S.btnPrimary} onClick={doImport} disabled={busy||!canImport}>
                  {busy?"Importing…":`💾 Import ${toImport.length} payment${toImport.length!==1?"s":""}`}
                </button>
              </div>
            </div>
          )}
          {step===3 && result && (
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontSize:49,marginBottom:12}}>✅</div>
              <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,marginBottom:20}}>Upload complete</div>
              <div style={{display:"grid",gridTemplateColumns:`repeat(${result.unresolved?4:3},1fr)`,gap:12,marginBottom:20,maxWidth:result.unresolved?560:420,margin:"0 auto 20px"}}>
                {[
                  {label:"Imported", value:result.inserted, color:"var(--accent)"},
                  {label:"Already existed", value:result.duplicates, color:"var(--ink3)"},
                  {label:"Skipped conflicts", value:result.skipped, color:"var(--danger)"},
                  ...(result.unresolved?[{label:"Skipped — unresolved", value:result.unresolved, color:"var(--warn)"}]:[]),
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:25,fontWeight:300,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>
              {result.unresolved>0 && (
                <div style={{maxWidth:420,margin:"0 auto 20px",padding:"10px 14px",background:"var(--warn-l)",borderRadius:8,fontSize:12,color:"var(--warn)",textAlign:"left"}}>
                  {result.unresolved} row{result.unresolved!==1?"s":""} were skipped for missing customer/bank matches. Add the missing customer (or bank mapping) and re-upload the same file — already-imported rows won't be duplicated.
                </div>
              )}
              <button style={S.btnPrimary} onClick={onClose}>✓ Done</button>
            </div>
          )}
        </div>
      </div>
      {addCustomerFor && (
        <QuickAddCustomerModal
          prefillName={addCustomerFor.name}
          onClose={()=>setAddCustomerFor(null)}
          onCreated={(cust)=>handleCustomerCreated(addCustomerFor.indices,cust)}
          toast={toast}
        />
      )}
    </div>
  );
}

// ── PAYMENTS PAGE ─────────────────────────────────────────────
export function PaymentsPage({ toast }) {
  const [payments, setPayments]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [modal, setModal]               = useState(false);
  const [uploadModal, setUploadModal]   = useState(false);
  const [dailyUploadModal, setDailyUploadModal] = useState(false);
  const [matchPayment, setMatchPayment] = useState(null);
  const [search, setSearch]             = useState("");
  const [modeFilter, setModeFilter]     = useState("all");
  const [fromDate, setFromDate]         = useState(""); // "" until the default (most recent) date is known
  const [toDate, setToDate]             = useState("");
  const [allDates, setAllDates]         = useState(false);
  const [defaultDate, setDefaultDate]   = useState("");

  // Was a hardcoded .limit(300) — with 61,000+ payments now on record that
  // silently showed only the 300 most recent, with no way to see or filter
  // to anything older. Now scoped to [fromDate,toDate] (both null when
  // allDates is set), fetched in full via pagination, same as Sales/Recon.
  const load = useCallback(async () => {
    if (!allDates && !(fromDate && toDate)) return; // scope not yet known
    setLoading(true);
    let all = [];
    let from = 0;
    const pageSize = 1000;
    while (true) {
      let q = supabase.from("customer_payments").select("*, customers(name,type)");
      if (!allDates && fromDate && toDate) q = q.gte("payment_date",fromDate).lte("payment_date",toDate);
      const { data, error } = await q
        .order("payment_date",{ascending:false}).order("created_at",{ascending:false})
        .range(from, from+pageSize-1);
      if (error || !data || data.length===0) break;
      all = [...all, ...data];
      if (data.length < pageSize) break;
      from += pageSize;
    }
    setPayments(all); setLoading(false);
  },[fromDate, toDate, allDates]);

  // Find the most recent payment date and scope the first load to it,
  // instead of loading everything (or the old flat 300) on every visit.
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("customer_payments")
        .select("payment_date").order("payment_date",{ascending:false}).limit(1);
      const latest = (data && data[0]) ? data[0].payment_date : new Date().toISOString().slice(0,10);
      setDefaultDate(latest);
      setFromDate(latest); setToDate(latest);
    })();
  }, []);

  useEffect(()=>{load();},[load]);

  const showAllDates = () => { setAllDates(true); setFromDate(""); setToDate(""); };
  const setFromDateScoped = (v) => { setAllDates(false); setFromDate(v); };
  const setToDateScoped   = (v) => { setAllDates(false); setToDate(v); };
  const resetToDefault = () => {
    setSearch(""); setModeFilter("all"); setAllDates(false);
    setFromDate(defaultDate); setToDate(defaultDate);
  };

  const deletePayment = async (id) => {
    if (!window.confirm("Delete this payment?")) return;
    await supabase.from("customer_payments").delete().eq("id",id);
    toast("Payment deleted.","error"); load();
  };

  const unconfirm = async (p) => {
    await supabase.from("customer_payments").update({bank_confirmed:false,bank_ref:null,confirmed_at:null}).eq("id",p.id);
    await supabase.from("bank_statement_entries").update({matched_payment_id:null}).eq("matched_payment_id",p.id);
    toast("Payment unconfirmed."); load();
  };

  const confirmCash = async (id) => {
    await supabase.from("customer_payments").update({bank_confirmed:true,confirmed_at:new Date().toISOString()}).eq("id",id);
    load();
  };

  // Corrects a wrongly-uploaded mode. A bank-confirmed payment is tied to a
  // statement entry of its old mode, so that match has to be released first
  // (same as Unconfirm) — otherwise it would stay "confirmed" against the
  // wrong bank's statement.
  const changeMode = async (p, newMode) => {
    if (!newMode || newMode===p.payment_mode) return;
    if (p.bank_confirmed && !window.confirm(`This payment is bank-confirmed against a ${MODE_LABELS[p.payment_mode]||p.payment_mode} statement entry.\n\nChanging the mode to ${MODE_LABELS[newMode]} will unconfirm it and release that statement entry. Continue?`)) return;
    try {
      if (p.bank_confirmed) {
        const {error:e1} = await supabase.from("bank_statement_entries").update({matched_payment_id:null}).eq("matched_payment_id",p.id);
        if (e1) throw e1;
      }
      const patch = {payment_mode:newMode};
      if (p.bank_confirmed) Object.assign(patch,{bank_confirmed:false,bank_ref:null,confirmed_at:null});
      const {data,error} = await supabase.from("customer_payments").update(patch).eq("id",p.id).select();
      if (error) throw error;
      if (!data || data.length===0) throw new Error("This payment no longer exists — refresh the page.");
      toast(`Mode changed to ${MODE_LABELS[newMode]}.`,"success");
      load();
    } catch(e) { toast("Could not change mode: "+e.message,"error"); load(); }
  };

  // Date range is already applied server-side by load() — only search and
  // mode are filtered client-side here.
  const filtered = payments.filter(p=>
    (p.customers?.name||"").toLowerCase().includes(search.toLowerCase())&&
    (modeFilter==="all"||p.payment_mode===modeFilter)
  );

  // Exports exactly what's filtered on screen (date range, search, mode) —
  // every matching payment, not just the current page.
  const downloadCSV = () => {
    if (filtered.length===0) { toast("No payments to download.","error"); return; }
    const headers = ["Date","Customer","Mode","Reference","Amount (₹)","Status","Bank ref","Confirmed on","Notes"];
    const rows = filtered.map(p=>[
      fmtDMY(p.payment_date), p.customers?.name||"", MODE_LABELS[p.payment_mode]||p.payment_mode||"",
      p.reference_no||"", parseFloat(p.amount||0).toFixed(2), p.bank_confirmed?"Confirmed":"Pending",
      p.bank_ref||"", p.confirmed_at?fmtDMY(p.confirmed_at.slice(0,10)):"", p.notes||"",
    ]);
    rows.push(["TOTAL","","","",filtered.reduce((s,p)=>s+parseFloat(p.amount||0),0).toFixed(2),"","","",""]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob(["﻿"+csv],{type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob); const link = document.createElement("a");
    const range = allDates ? "all-dates" : (fromDate===toDate ? fromDate : `${fromDate}_to_${toDate}`);
    link.href=url; link.download=`payments-${range}.csv`; link.click(); URL.revokeObjectURL(url);
  };

  const totalFiltered  = filtered.reduce((s,p)=>s+parseFloat(p.amount||0),0);
  const totalConfirmed = filtered.filter(p=>p.bank_confirmed).reduce((s,p)=>s+parseFloat(p.amount||0),0);
  const totalPending   = filtered.filter(p=>!p.bank_confirmed).reduce((s,p)=>s+parseFloat(p.amount||0),0);

  // Pagination — same reasoning as the Sales page: "Show all dates" can
  // pull in tens of thousands of rows, which shouldn't all render at once.
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, modeFilter, payments]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage-1)*PAGE_SIZE, currentPage*PAGE_SIZE);

  return (
    <div>
      <div style={S.pageHeader}>
        <div><div style={S.pageTitle}>Payments</div><div style={S.subtitle}>
  All customer payments · {payments.length} total
  {allDates ? " · all dates" : fromDate ? (fromDate===toDate ? ` · ${fmtDMY(fromDate)}` : ` · ${fmtDMY(fromDate)} – ${fmtDMY(toDate)}`) : ""}
</div></div>
        <div style={{display:"flex",gap:8}}>
          <button style={S.btnGhost} onClick={()=>setUploadModal(true)}>⬆ Upload statement</button>
          <button style={S.btnGhost} onClick={downloadCSV}>⬇ CSV</button>
          <button style={S.btnGhost} onClick={()=>setDailyUploadModal(true)}>⬆ Upload daily receivables</button>
          <button style={S.btnPrimary} onClick={()=>setModal(true)}>+ Record payment</button>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:28}}>
        {[
          {label:"Total received",  value:fmt(totalFiltered),  color:"var(--accent2)"},
          {label:"Bank confirmed",  value:fmt(totalConfirmed), color:"var(--accent)"},
          {label:"Pending confirm", value:fmt(totalPending),   color:"var(--warn)"},
          {label:"Transactions",    value:filtered.length,     color:"var(--info)"},
        ].map(s=>(
          <div key={s.label} style={S.statCard(s.color)}>
            <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,lineHeight:1}}>{s.value}</div>
            <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{background:"#fff",border:"1px solid var(--paper3)",borderRadius:10,padding:"16px 20px",marginBottom:20,boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={{...S.label,display:"block",marginBottom:5}}>Search customer</label>
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--ink3)",fontSize:14,pointerEvents:"none"}}>🔍</span>
              <input placeholder="Customer name…" value={search} onChange={e=>setSearch(e.target.value)} style={{...S.input,paddingLeft:34,background:"#fff"}}/>
            </div>
          </div>
          <div>
            <label style={{...S.label,display:"block",marginBottom:5}}>From date</label>
            <input type="date" value={fromDate} onChange={e=>setFromDateScoped(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={{...S.label,display:"block",marginBottom:5}}>To date</label>
            <input type="date" value={toDate} onChange={e=>setToDateScoped(e.target.value)} style={S.input}/>
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          {["all",...PAYMENT_MODES].map(m=>(
            <button key={m} onClick={()=>setModeFilter(m)}
              style={{padding:"6px 12px",borderRadius:20,fontSize:12,cursor:"pointer",border:"1px solid var(--paper3)",background:modeFilter===m?"var(--accent)":"#fff",color:modeFilter===m?"#fff":"var(--ink2)",fontFamily:"var(--mono)"}}>
              {m==="all"?"All":MODE_LABELS[m]}
            </button>
          ))}
          {!allDates&&<button style={S.btnSm} onClick={showAllDates}>⬆ Show all dates</button>}
          {(search||modeFilter!=="all"||allDates||fromDate!==defaultDate||toDate!==defaultDate)&&(
            <button style={S.btnSm} onClick={resetToDefault}>✕ Reset to {defaultDate?fmtDMY(defaultDate):"latest"}</button>
          )}
          <span style={{fontSize:12,color:"var(--ink3)",marginLeft:"auto"}}>{filtered.length} payment{filtered.length!==1?"s":""}</span>
        </div>
      </div>

      <div style={S.card}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
            <th style={S.th()}>Date</th><th style={S.th()}>Customer</th><th style={S.th()}>Mode</th>
            <th style={S.th()}>Reference</th><th style={S.th(true)}>Amount</th><th style={S.th()}>Status</th><th style={S.th()}></th>
          </tr></thead>
          <tbody>
            {loading&&<tr><td colSpan={7} style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>
              {allDates?"Loading all payments…":`Loading payments for ${fromDate?fmtDMY(fromDate):"…"}${fromDate!==toDate?` – ${toDate?fmtDMY(toDate):"…"}`:""}`}
            </td></tr>}
            {!loading&&filtered.length===0&&(
              <tr><td colSpan={7}><div style={{textAlign:"center",padding:"60px 20px",color:"var(--ink3)"}}>
                <div style={{fontSize:37,marginBottom:12,opacity:0.5}}>💳</div>
                <p style={{fontSize:14}}>No payments yet.</p>
              </div></td></tr>
            )}
            {!loading&&pageRows.map(p=>(
              <tr key={p.id} style={{borderBottom:"1px solid var(--paper2)"}}>
                <td style={{...S.td,fontFamily:"var(--mono)",color:"var(--ink2)"}}>{fmtDMY(p.payment_date)}</td>
                <td style={S.td}>
                  <div style={{fontWeight:500}}>{p.customers?.name||"—"}</div>
                  {p.notes&&<div style={{fontSize:11,color:"var(--ink3)",marginTop:2}}>{p.notes}</div>}
                </td>
                <td style={S.td}>
                  <select value={p.payment_mode} onChange={e=>changeMode(p,e.target.value)} title="Change payment mode"
                    style={{padding:"3px 6px",fontSize:11,fontWeight:500,borderRadius:8,border:"1px solid var(--paper3)",background:"var(--paper2)",color:"var(--ink2)",fontFamily:"var(--mono)",cursor:"pointer",width:"auto"}}>
                    {!PAYMENT_MODES.includes(p.payment_mode)&&<option value={p.payment_mode}>{p.payment_mode}</option>}
                    {PAYMENT_MODES.map(m=><option key={m} value={m}>{MODE_LABELS[m]}</option>)}
                  </select>
                </td>
                <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12,color:"var(--ink3)"}}>{p.reference_no||p.bank_ref||"—"}</td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500}}>{fmt(p.amount)}</td>
                <td style={S.td}>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,background:p.bank_confirmed?"var(--accent-l)":"var(--warn-l)",color:p.bank_confirmed?"var(--accent)":"var(--warn)"}}>
                      {p.bank_confirmed?"✓ Confirmed":"Pending"}
                    </span>
                    {!p.bank_confirmed&&(
                      NO_STATEMENT_MODES.includes(p.payment_mode)
                        ? <button style={{padding:"3px 8px",fontSize:11,background:"transparent",border:"1px solid var(--paper3)",borderRadius:6,cursor:"pointer",fontFamily:"var(--mono)",color:"var(--ink2)"}} onClick={()=>confirmCash(p.id)}>Confirm</button>
                        : <button style={{padding:"3px 8px",fontSize:11,background:"var(--info)",color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontFamily:"var(--mono)",fontWeight:500}} onClick={()=>setMatchPayment(p)}>Match statement</button>
                    )}
                    {p.bank_confirmed&&<button style={{padding:"3px 8px",fontSize:11,background:"transparent",border:"1px solid var(--paper3)",borderRadius:6,cursor:"pointer",fontFamily:"var(--mono)",color:"var(--ink3)"}} onClick={()=>unconfirm(p)}>Unconfirm</button>}
                  </div>
                </td>
                <td style={S.td}>
                  <div style={{display:"flex",gap:6}}>
                    <button style={{padding:"4px 8px",fontSize:12,background:"transparent",color:"var(--ink2)",border:"1px solid var(--paper3)",borderRadius:6,cursor:"pointer",fontFamily:"var(--mono)"}} onClick={()=>setModal(p)}>✎</button>
                    <button style={{padding:"4px 8px",fontSize:12,background:"transparent",color:"var(--danger)",border:"1px solid var(--danger-l)",borderRadius:6,cursor:"pointer",fontFamily:"var(--mono)"}} onClick={()=>deletePayment(p.id)}>✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading&&filtered.length>PAGE_SIZE&&(
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:14,padding:"0 4px"}}>
          <span style={{fontSize:12,color:"var(--ink3)"}}>
            Showing {(currentPage-1)*PAGE_SIZE+1}–{Math.min(currentPage*PAGE_SIZE,filtered.length)} of {filtered.length}
          </span>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <button style={{...S.btnSm,opacity:currentPage<=1?0.4:1,cursor:currentPage<=1?"default":"pointer"}} disabled={currentPage<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}>← Prev</button>
            <span style={{fontSize:12,color:"var(--ink3)",fontFamily:"var(--mono)"}}>Page {currentPage} of {totalPages}</span>
            <button style={{...S.btnSm,opacity:currentPage>=totalPages?0.4:1,cursor:currentPage>=totalPages?"default":"pointer"}} disabled={currentPage>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>Next →</button>
          </div>
        </div>
      )}

      {modal&&<AddPaymentModal record={modal===true?null:modal} onClose={()=>setModal(false)} onSave={()=>{const wasEdit=modal!==true;setModal(false);load();toast(wasEdit?"Payment updated!":"Payment recorded!","success");}}/>}
      {uploadModal&&<StatementUploadModal onClose={()=>setUploadModal(false)} onSave={()=>{setUploadModal(false);}} toast={toast}/>}
      {dailyUploadModal&&<DailyReceivableUploadModal onClose={()=>setDailyUploadModal(false)} onDone={()=>{load();}} toast={toast}/>}
      {matchPayment&&<MatchStatementModal payment={matchPayment} onClose={()=>setMatchPayment(null)} onMatched={()=>{setMatchPayment(null);load();}} toast={toast}/>}
    </div>
  );
}

// ── LEDGER PAGE ───────────────────────────────────────────────
export function LedgerPage({ toast }) {
  const [customers, setCustomers]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState("");
  const [selected, setSelected]           = useState(null);
  const [detail, setDetail]               = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [addPayment, setAddPayment]       = useState(false);

const load = useCallback(async()=>{
  setLoading(true);
  let all = [];
  let from = 0;
  const pageSize = 1000;
  while (true) {
    const { data, error } = await supabase.from("customer_ledger")
      .select("*")
      .order("customer_name")
      .range(from, from + pageSize - 1);
    if (error || !data || data.length === 0) break;
    all = [...all, ...data];
    if (data.length < pageSize) break;
    from += pageSize;
  }
  setCustomers(all); setLoading(false);
},[]);

  useEffect(()=>{load();},[load]);

  const openDetail = async(cust)=>{
    setSelected(cust); setDetailLoading(true);
    const [invRes,payRes] = await Promise.all([
      supabase.from("invoices").select("invoice_number,invoice_date,total,status").eq("customer_id",cust.customer_id).eq("is_deleted",false).order("invoice_date",{ascending:false}),
      supabase.from("customer_payments").select("*").eq("customer_id",cust.customer_id).order("payment_date",{ascending:false}),
    ]);
    const entries=[];
    (invRes.data||[]).forEach(inv=>{entries.push({date:inv.invoice_date,type:"invoice",ref:inv.invoice_number,debit:parseFloat(inv.total)||0,credit:0});});
    (payRes.data||[]).forEach(pay=>{entries.push({date:pay.payment_date,type:"payment",ref:MODE_LABELS[pay.payment_mode]+(pay.reference_no?" · "+pay.reference_no:""),debit:0,credit:parseFloat(pay.amount)||0,confirmed:pay.bank_confirmed});});
    entries.sort((a,b)=>a.date>b.date?1:-1);
    let bal=0; entries.forEach(e=>{bal+=e.debit-e.credit;e.balance=bal;}); entries.reverse();
    setDetail({entries}); setDetailLoading(false);
  };
const downloadLedger = (cust, entries) => {
  const lines = [];
  lines.push([`Ledger Statement — ${cust.customer_name}`]);
  lines.push([`Customer type: ${cust.customer_type}`]);
  lines.push([`Generated: ${new Date().toLocaleDateString("en-IN")}`]);
  lines.push([]);
  lines.push(["Date","Type","Reference","Debit (₹)","Credit (₹)","Balance (₹)"]);
  [...entries].reverse().forEach(e => {
    lines.push([
      fmtDMY(e.date),
      e.type==="invoice" ? "Invoice" : "Payment",
      e.ref,
      e.debit>0 ? parseFloat(e.debit).toFixed(2) : "",
      e.credit>0 ? parseFloat(e.credit).toFixed(2) : "",
      parseFloat(Math.abs(e.balance)).toFixed(2)+(e.balance<0?" Cr":""),
    ]);
  });
  lines.push([]);
  lines.push(["","","Total invoiced",fmt(cust.total_invoiced),"",""]);
  lines.push(["","","Total received","",fmt(cust.total_paid),""]);
  lines.push(["","","Balance due",fmt(cust.balance_due),"",""]);
  const csv = lines.map(r=>r.map(v=>`"${String(v||"").replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href=url; link.download=`ledger-${cust.customer_name.replace(/\s+/g,"-")}-${new Date().toISOString().slice(0,10)}.csv`; link.click();
  URL.revokeObjectURL(url);
};
  const filtered=customers.filter(c=>(c.customer_name||"").toLowerCase().includes(search.toLowerCase()));
  const totInv=customers.reduce((s,c)=>s+parseFloat(c.total_invoiced||0),0);
  const totPaid=customers.reduce((s,c)=>s+parseFloat(c.total_paid||0),0);
  const totOut=customers.reduce((s,c)=>s+parseFloat(c.balance_due||0),0);

  return (
    <div>
      <div style={S.pageHeader}>
        <div><div style={S.pageTitle}>Ledger</div><div style={S.subtitle}>Customer account balances</div></div>
        <button style={S.btnPrimary} onClick={()=>setAddPayment(true)}>+ Record payment</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:28}}>
        {[{label:"Total invoiced",value:fmt(totInv),color:"var(--info)"},{label:"Total received",value:fmt(totPaid),color:"var(--accent)"},{label:"Total outstanding",value:fmt(totOut),color:"var(--danger)"}].map(s=>(
          <div key={s.label} style={S.statCard(s.color)}>
            <div style={{fontFamily:"var(--serif)",fontSize:25,fontWeight:300,lineHeight:1}}>{s.value}</div>
            <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:selected?"1fr 1.6fr":"1fr",gap:20}}>
        <div>
          <div style={{marginBottom:12,position:"relative"}}>
            <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--ink3)",fontSize:14,pointerEvents:"none"}}>🔍</span>
            <input placeholder="Search customer…" value={search} onChange={e=>setSearch(e.target.value)} style={{...S.input,paddingLeft:34,background:"#fff"}}/>
          </div>
          <div style={S.card}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                <th style={S.th()}>Customer</th><th style={S.th(true)}>Invoiced</th><th style={S.th(true)}>Paid</th><th style={S.th(true)}>Balance</th>
              </tr></thead>
              <tbody>
                {loading&&<tr><td colSpan={4} style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>Loading…</td></tr>}
                {!loading&&filtered.length===0&&<tr><td colSpan={4} style={{padding:40,textAlign:"center",color:"var(--ink3)",fontSize:13}}>No customers found.</td></tr>}
                {!loading&&filtered.map(c=>(
                  <tr key={c.customer_id} onClick={()=>openDetail(c)}
                    style={{borderBottom:"1px solid var(--paper2)",cursor:"pointer",background:selected?.customer_id===c.customer_id?"var(--accent-l)":"transparent",transition:"background 0.1s"}}>
                    <td style={S.td}><div style={{fontWeight:500}}>{c.customer_name}</div><div style={{fontSize:11,color:"var(--ink3)"}}>{c.customer_type}</div></td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12,color:"var(--ink2)"}}>{fmt(c.total_invoiced)}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12,color:"var(--accent)"}}>{fmt(c.total_paid)}</td>
                    <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:parseFloat(c.balance_due)>0?"var(--danger)":"var(--accent)"}}>{fmt(c.balance_due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {selected&&(
          <div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div><div style={{fontWeight:500,fontSize:15}}>{selected.customer_name}</div><div style={{fontSize:12,color:"var(--ink3)"}}>{selected.customer_type}</div></div>
            <div style={{display:"flex",gap:8}}>
  {detail&&<button style={S.btnGhost} onClick={()=>downloadLedger(selected,detail.entries)}>⬇ Download ledger</button>}
  <button style={S.btnPrimary} onClick={()=>setAddPayment(selected)}>+ Payment</button>
  <button style={S.btnSm} onClick={()=>{setSelected(null);setDetail(null);}}>✕</button>
</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
              {[{label:"Invoiced",value:fmt(selected.total_invoiced),color:"var(--info)"},{label:"Paid",value:fmt(selected.total_paid),color:"var(--accent)"},{label:"Balance due",value:fmt(selected.balance_due),color:parseFloat(selected.balance_due)>0?"var(--danger)":"var(--accent)"}].map(s=>(
                <div key={s.label} style={{background:"#fff",borderRadius:8,padding:"12px 14px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)",borderLeft:`3px solid ${s.color}`}}>
                  <div style={{fontFamily:"var(--mono)",fontSize:14,fontWeight:500,color:s.color}}>{s.value}</div>
                  <div style={{fontSize:11,color:"var(--ink3)",marginTop:3,textTransform:"uppercase",letterSpacing:"0.5px"}}>{s.label}</div>
                </div>
              ))}
            </div>
            {detailLoading&&<div style={{textAlign:"center",padding:30,color:"var(--ink3)"}}>Loading…</div>}
            {!detailLoading&&detail&&(
              <div style={{...S.card,maxHeight:500,overflowY:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0}}>
                    <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                      <th style={S.th()}>Date</th><th style={S.th()}>Type</th><th style={S.th()}>Reference</th>
                      <th style={S.th(true)}>Debit</th><th style={S.th(true)}>Credit</th><th style={S.th(true)}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.entries.length===0&&<tr><td colSpan={6} style={{padding:30,textAlign:"center",color:"var(--ink3)",fontSize:13}}>No transactions yet.</td></tr>}
                    {detail.entries.map((e,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--paper2)",background:e.type==="payment"?"#f9fff9":"#fff"}}>
                        <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12,color:"var(--ink3)"}}>{fmtDMY(e.date)}</td>
                        <td style={S.td}>
                          <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:e.type==="invoice"?"var(--info-l)":"var(--accent-l)",color:e.type==="invoice"?"var(--info)":"var(--accent)"}}>
                            {e.type==="invoice"?"Invoice":"Payment"}
                          </span>
                          {e.type==="payment"&&!e.confirmed&&<span style={{marginLeft:4,display:"inline-block",padding:"1px 5px",borderRadius:8,fontSize:10,background:"var(--warn-l)",color:"var(--warn)"}}>Unconfirmed</span>}
                        </td>
                        <td style={{...S.td,fontSize:12,color:"var(--ink2)"}}>{e.ref}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12,color:e.debit>0?"var(--danger)":"var(--ink3)"}}>{e.debit>0?fmt(e.debit):"—"}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12,color:e.credit>0?"var(--accent)":"var(--ink3)"}}>{e.credit>0?fmt(e.credit):"—"}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:e.balance>0?"var(--danger)":e.balance<0?"var(--accent)":"var(--ink3)"}}>
                          {fmt(Math.abs(e.balance))}{e.balance<0?" Cr":""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      {addPayment&&<AddPaymentModal preselectedCustomer={addPayment===true?null:addPayment} onClose={()=>setAddPayment(false)} onSave={()=>{setAddPayment(false);load();if(selected)openDetail(selected);toast("Payment recorded!","success");}}/>}
    </div>
  );
}

// ── RECON PAGE ────────────────────────────────────────────────
async function fetchAllRows(table, select, filterFn) {
  let all = []; let from = 0; const pageSize = 1000;
  while (true) {
    let q = supabase.from(table).select(select);
    if (filterFn) q = filterFn(q);
    const { data, error } = await q.range(from, from+pageSize-1);
    if (error) throw error;
    if (!data || data.length===0) break;
    all = [...all, ...data];
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

export function ReconPage({ toast }) {
  const [data, setData]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [listLoaded, setListLoaded]     = useState(false);
  const [singleCustomerId, setSingleCustomerId] = useState(null);
  const [grandTotals, setGrandTotals]   = useState(null);
  const [grandTotalsLoading, setGrandTotalsLoading] = useState(true);
  const [search, setSearch]             = useState("");
  const [filter, setFilter]             = useState("all");
  const [showInvoiced, setShowInvoiced] = useState(false);
  const [fromDate, setFromDate]         = useState("");
  const [toDate, setToDate]             = useState("");
  const [dateData, setDateData]         = useState({});
  const [dateLoading, setDateLoading]   = useState(false);

  // Lightweight — just id/name/type for the "look up one customer" search,
  // separate from the heavy per-customer_recon aggregate below.
  const [pickerCustomers, setPickerCustomers] = useState([]);
  const [customerQ, setCustomerQ]       = useState("");
  const [custOpen, setCustOpen]         = useState(false);

  useEffect(() => {
    (async () => {
      let all = [];
      let from = 0;
      while (true) {
        const { data, error } = await supabase.from("customers")
          .select("id,name,type").eq("is_active",true).order("name")
          .range(from, from+999);
        if (error || !data || data.length===0) break;
        all = [...all, ...data];
        if (data.length<1000) break;
        from += 1000;
      }
      setPickerCustomers(all);
    })();
  }, []);
  const filteredPickerCustomers = customerQ
    ? matchItems(pickerCustomers, customerQ, c=>c.name, 50).list
    : [];

const load = useCallback(async()=>{
  setLoading(true);
  let all = [];
  let from = 0;
  const pageSize = 1000;
  while (true) {
    const { data, error } = await supabase.from("customer_recon")
      .select("*")
      .range(from, from + pageSize - 1);
    if (error || !data || data.length === 0) break;
    all = [...all, ...data];
    if (data.length < pageSize) break;
    from += pageSize;
  }
  setData(all); setLoading(false); setListLoaded(true);
},[]);

  const showAllCustomers = () => { setSingleCustomerId(null); load(); };

  const loadSingleCustomer = async (c) => {
    setLoading(true);
    setSingleCustomerId(c.id);
    setCustomerQ(c.name);
    setCustOpen(false);
    const { data, error } = await supabase.from("customer_recon").select("*").eq("customer_id", c.id).maybeSingle();
    setData(!error && data ? [data] : []);
    setListLoaded(true);
    setLoading(false);
  };

  const resetToIdle = () => {
    setListLoaded(false); setSingleCustomerId(null); setData([]);
    setCustomerQ(""); setFromDate(""); setToDate("");
  };

  // Cheap aggregate for the two top-line numbers, shown immediately without
  // paging through every customer row — the full per-customer list (1000+
  // rows) only loads when the user explicitly asks for it below.
  useEffect(() => {
    setGrandTotalsLoading(true);
    supabase.rpc("recon_totals").then(({data,error}) => {
      if (!error && data && data[0]) setGrandTotals(data[0]);
      setGrandTotalsLoading(false);
    });
  }, []);

  // Date-scoped recomputation: customer_recon is a pre-aggregated view with no
  // per-row date, so a date range needs its own pass over the underlying
  // tables (work_orders + wo_line_items, customer_payments,
  // historical_transactions, customer_opening_balances) — same three-source
  // composition the view itself uses (opening balance + WO sales +
  // historical migration data), just date-scoped.
  useEffect(() => {
    if (!toDate) { setDateData({}); return; }
    let cancelled = false;
    (async () => {
      setDateLoading(true);
      try {
        // Scoped to just the picked customer when one is selected, so a
        // single-customer lookup never pulls every customer's rows just to
        // compute a date range for the one row actually on screen.
        const scope = singleCustomerId ? (q => q.eq("customer_id", singleCustomerId)) : (q => q);
        const [wos, payments, historical, openings] = await Promise.all([
          fetchAllRows("work_orders","id,customer_id,order_date,is_deleted,status", scope),
          fetchAllRows("customer_payments","customer_id,payment_date,amount", scope),
          fetchAllRows("historical_transactions","customer_id,txn_date,txn_type,amount", scope),
          fetchAllRows("customer_opening_balances","customer_id,opening_sales,opening_receipts", scope),
        ]);
        const activeWOs = wos.filter(w => !w.is_deleted && w.status!=="cancelled");
        const woIds = activeWOs.map(w=>w.id);
        let lineItems = [];
        for (let i=0;i<woIds.length;i+=200) {
          const chunk = woIds.slice(i,i+200);
          if (!chunk.length) continue;
          const li = await fetchAllRows("wo_line_items","work_order_id,quantity,unit_price", q=>q.in("work_order_id",chunk));
          lineItems = lineItems.concat(li);
        }
        const woTotal = {};
        lineItems.forEach(l => { woTotal[l.work_order_id] = (woTotal[l.work_order_id]||0) + (parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0); });

        const m = {};
        const ensure = (cid) => { if (!m[cid]) m[cid] = {periodSales:0,periodReceipts:0,asOfSales:0,asOfReceipts:0}; return m[cid]; };

        activeWOs.forEach(w => {
          const amt = woTotal[w.id]||0;
          if (w.order_date <= toDate) ensure(w.customer_id).asOfSales += amt;
          if (fromDate && w.order_date>=fromDate && w.order_date<=toDate) ensure(w.customer_id).periodSales += amt;
        });
        historical.forEach(h => {
          const amt = parseFloat(h.amount)||0;
          const bucket = h.txn_type==="receipt" ? "Receipts" : "Sales";
          if (h.txn_date <= toDate) ensure(h.customer_id)[`asOf${bucket}`] += amt;
          if (fromDate && h.txn_date>=fromDate && h.txn_date<=toDate) ensure(h.customer_id)[`period${bucket}`] += amt;
        });
        payments.forEach(p => {
          const amt = parseFloat(p.amount)||0;
          if (p.payment_date <= toDate) ensure(p.customer_id).asOfReceipts += amt;
          if (fromDate && p.payment_date>=fromDate && p.payment_date<=toDate) ensure(p.customer_id).periodReceipts += amt;
        });
        openings.forEach(o => {
          const e = ensure(o.customer_id);
          e.asOfSales += parseFloat(o.opening_sales||0);
          e.asOfReceipts += parseFloat(o.opening_receipts||0);
        });

        if (!cancelled) setDateData(m);
      } catch(e) { if (!cancelled) toast("Failed to compute date range: "+e.message,"error"); }
      finally { if (!cancelled) setDateLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [fromDate, toDate, singleCustomerId, toast]);

  const filtered=data.filter(r=>{
    const ms=(r.customer_name||"").toLowerCase().includes(search.toLowerCase());
    const mf=filter==="all"||(filter==="outstanding"&&parseFloat(r.outstanding)>0)||(filter==="settled"&&parseFloat(r.outstanding)<=0);
    return ms&&mf;
  }).map(r => {
    const d = dateData[r.customer_id];
    return d ? { ...r,
      period_sales: d.periodSales, period_receipts: d.periodReceipts,
      outstanding_as_of: d.asOfSales - d.asOfReceipts,
    } : r;
  });

  const showPeriod = !!(fromDate && toDate);
  const showAsOf = !!toDate;

  const totals={
    sales:            filtered.reduce((s,r)=>s+parseFloat(r.total_sales||0),0),
    invoiced:         filtered.reduce((s,r)=>s+parseFloat(r.total_invoiced||0),0),
    receipts:         filtered.reduce((s,r)=>s+parseFloat(r.total_receipts||0),0),
    outstanding:      filtered.reduce((s,r)=>s+parseFloat(r.outstanding||0),0),
    outstandingSales: filtered.reduce((s,r)=>s+(parseFloat(r.total_sales||0)-parseFloat(r.total_receipts||0)),0),
    periodSales:      filtered.reduce((s,r)=>s+parseFloat(r.period_sales||0),0),
    periodReceipts:   filtered.reduce((s,r)=>s+parseFloat(r.period_receipts||0),0),
    asOfOutstanding:  filtered.reduce((s,r)=>s+parseFloat(r.outstanding_as_of||0),0),
  };

  const downloadCSV=()=>{
    const headers=["Customer","Type","Total Sales",...(showInvoiced?["Total Invoiced"]:[]),"Total Receipts","Balance",...(showInvoiced?["Outstanding (Invoiced)"]:[]),...(showPeriod?["Period Sales","Period Receipts"]:[]),...(showAsOf?[`Outstanding (as of ${fmtDMY(toDate)})`]:[])] ;
    const rows=filtered.map(r=>[r.customer_name,r.customer_type,parseFloat(r.total_sales||0).toFixed(2),...(showInvoiced?[parseFloat(r.total_invoiced||0).toFixed(2)]:[]),parseFloat(r.total_receipts||0).toFixed(2),(parseFloat(r.total_sales||0)-parseFloat(r.total_receipts||0)).toFixed(2),...(showInvoiced?[parseFloat(r.outstanding||0).toFixed(2)]:[]),...(showPeriod?[parseFloat(r.period_sales||0).toFixed(2),parseFloat(r.period_receipts||0).toFixed(2)]:[]),...(showAsOf?[parseFloat(r.outstanding_as_of||0).toFixed(2)]:[])] );
    rows.push(["TOTAL","",totals.sales.toFixed(2),...(showInvoiced?[totals.invoiced.toFixed(2)]:[]),totals.receipts.toFixed(2),totals.outstandingSales.toFixed(2),...(showInvoiced?[totals.outstanding.toFixed(2)]:[]),...(showPeriod?[totals.periodSales.toFixed(2),totals.periodReceipts.toFixed(2)]:[]),...(showAsOf?[totals.asOfOutstanding.toFixed(2)]:[])] );
    const csv=[headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob=new Blob([csv],{type:"text/csv;charset=utf-8;"});
    const url=URL.createObjectURL(blob); const link=document.createElement("a");
    link.href=url;link.download=`recon-${new Date().toISOString().slice(0,10)}.csv`;link.click();URL.revokeObjectURL(url);
  };



// Same three sources the customer_recon view itself totals up (opening
// balance + work-order line items + historical_transactions, the table
// that both the one-time bulk migration AND daily sales/receivable
// uploads write into) plus customer_payments — so the downloaded ledger's
// own totals always match what's shown on screen. Sales and receipts are
// consolidated to one figure per date (not one row per work order/upload
// row), matching the old system's statement format.
const downloadCustomerRecon = async (r) => {
  const [wos, historical, pays, obRows] = await Promise.all([
    fetchAllRows("work_orders","id,order_date", q=>q.eq("customer_id",r.customer_id).eq("is_deleted",false).neq("status","cancelled")),
    fetchAllRows("historical_transactions","txn_date,txn_type,amount", q=>q.eq("customer_id",r.customer_id)),
    fetchAllRows("customer_payments","payment_date,amount", q=>q.eq("customer_id",r.customer_id)),
    fetchAllRows("customer_opening_balances","opening_sales,opening_receipts", q=>q.eq("customer_id",r.customer_id)),
  ]);

  const woIds = wos.map(w=>w.id).filter(Boolean);
  let lineItems = [];
  if (woIds.length > 0) {
    lineItems = await fetchAllRows("wo_line_items","work_order_id,quantity,unit_price", q=>q.in("work_order_id",woIds));
  }
  const woTotals = {};
  lineItems.forEach(l => {
    woTotals[l.work_order_id] = (woTotals[l.work_order_id]||0) + (parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0);
  });

  // One bucket per date, combining every source that lands on that date.
  const byDate = {};
  const ensure = (d) => { if (!byDate[d]) byDate[d] = { sale:0, receipt:0 }; return byDate[d]; };
  wos.forEach(w => { ensure(w.order_date).sale += woTotals[w.id]||0; });
  historical.forEach(h => {
    const amt = parseFloat(h.amount)||0;
    ensure(h.txn_date)[h.txn_type==="receipt" ? "receipt" : "sale"] += amt;
  });
  pays.forEach(p => { ensure(p.payment_date).receipt += parseFloat(p.amount)||0; });

  const dates = Object.keys(byDate).sort();

  const ob = obRows[0]||null;
  const openingSales    = ob ? parseFloat(ob.opening_sales||0)    : 0;
  const openingReceipts = ob ? parseFloat(ob.opening_receipts||0) : 0;

  // Same sign convention used everywhere else in the app (Opening Balances
  // tab, Ledger page): balance = sales − receipts, positive = customer
  // owes (Dr), negative = credit in the customer's favour (Cr).
  let balance = openingSales - openingReceipts;
  const fmtBal = (b) => parseFloat(Math.abs(b)).toFixed(2) + (b<0?" Cr":b>0?" Dr":"");

  const lines = [];
  lines.push([`Sales & Receipts Statement`]);
  lines.push([`Customer: ${r.customer_name}`]);
  lines.push([`Type: ${r.customer_type}`]);
  lines.push([`Generated: ${new Date().toLocaleDateString("en-IN")}`]);
  lines.push([]);
  lines.push(["","Opening Sales","",openingSales.toFixed(2)]);
  lines.push(["","Opening Receipts","",openingReceipts.toFixed(2)]);
  lines.push(["","Opening Balance","",fmtBal(balance)]);
  lines.push([]);
  lines.push(["Date","Sale (₹)","Received (₹)","Balance (₹)"]);

  dates.forEach(d => {
    const { sale, receipt } = byDate[d];
    if (sale) {
      balance += sale;
      lines.push([fmtDMY(d), sale.toFixed(2), "", fmtBal(balance)]);
    }
    if (receipt) {
      balance -= receipt;
      lines.push([fmtDMY(d), "", receipt.toFixed(2), fmtBal(balance)]);
    }
  });

  lines.push([]);
  lines.push(["","Total Sales","",parseFloat(r.total_sales).toFixed(2)]);
  lines.push(["","Total Receipts","",parseFloat(r.total_receipts).toFixed(2)]);
  lines.push(["","Total Balance","",fmtBal(balance)]);

  const csv = lines.map(row=>row.map(v=>`"${String(v??"").replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href=url;
  link.download=`statement-${r.customer_name.replace(/\s+/g,"-")}-${new Date().toISOString().slice(0,10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

  const statCards=[
    {label:"Total sales",value:fmt(totals.sales),color:"var(--info)"},
    ...(showInvoiced?[{label:"Total invoiced",value:fmt(totals.invoiced),color:"#6a1b9a"}]:[]),
    {label:"Total receipts",value:fmt(totals.receipts),color:"var(--accent)"},
    {label:"Balance",value:fmt(totals.outstandingSales),color:totals.outstandingSales>0?"var(--danger)":"var(--accent)"},
    ...(showInvoiced?[{label:"Outstanding (invoiced)",value:fmt(totals.outstanding),color:"var(--danger)"}]:[]),
    ...(showPeriod?[{label:"Period sales",value:fmt(totals.periodSales),color:"var(--info)"},{label:"Period receipts",value:fmt(totals.periodReceipts),color:"var(--accent)"}]:[]),
    ...(showAsOf?[{label:`Outstanding as of ${fmtDMY(toDate)}`,value:fmt(totals.asOfOutstanding),color:"var(--danger)"}]:[]),
  ];
  const totalCols = 7 + (showInvoiced?2:0) + (showPeriod?2:0) + (showAsOf?1:0);

  const cardsToShow = listLoaded ? statCards : [
    {label:"Total sales",    value: grandTotalsLoading?"…":fmt(grandTotals?.total_sales||0),    color:"var(--info)"},
    {label:"Total receipts", value: grandTotalsLoading?"…":fmt(grandTotals?.total_receipts||0), color:"var(--accent)"},
    {label:"Balance",        value: grandTotalsLoading?"…":fmt((grandTotals?.total_sales||0)-(grandTotals?.total_receipts||0)), color:((grandTotals?.total_sales||0)-(grandTotals?.total_receipts||0))>0?"var(--danger)":"var(--accent)"},
  ];

  return (
    <div>
      <div style={S.pageHeader}>
        <div><div style={S.pageTitle}>Reconciliation</div><div style={S.subtitle}>Sales vs receipts per customer</div></div>
        {listLoaded&&<button style={S.btnGhost} onClick={downloadCSV}>⬇ Download CSV</button>}
      </div>
      <div style={{display:"grid",gridTemplateColumns:`repeat(${cardsToShow.length},1fr)`,gap:16,marginBottom:28}}>
        {cardsToShow.map(s=>(
          <div key={s.label} style={S.statCard(s.color)}>
            <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300,lineHeight:1}}>{s.value}</div>
            <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
          </div>
        ))}
      </div>
      {!listLoaded&&(
        <div style={{...S.card,padding:"40px 20px"}}>
          <p style={{fontSize:14,color:"var(--ink3)",textAlign:"center",marginBottom:18}}>Per-customer breakdown, search &amp; filters aren't loaded by default — look up one customer, or load everyone.</p>
          <div style={{maxWidth:380,margin:"0 auto"}}>
            <label style={{...S.label,display:"block",marginBottom:5}}>Look up one customer</label>
            <div style={{position:"relative"}}>
              <input value={customerQ} onChange={e=>{setCustomerQ(e.target.value);setCustOpen(true);}}
                onFocus={()=>setCustOpen(true)} placeholder="Type a customer name…" style={{...S.input,background:"#fff"}}/>
              {custOpen&&filteredPickerCustomers.length>0&&(
                <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid var(--paper3)",borderRadius:8,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",zIndex:50,maxHeight:220,overflowY:"auto",marginTop:2}}>
                  {filteredPickerCustomers.map(c=>(
                    <div key={c.id} style={{padding:"9px 12px",fontSize:13,cursor:"pointer",borderBottom:"1px solid var(--paper2)"}}
                      onMouseDown={()=>loadSingleCustomer(c)}>
                      <div style={{fontWeight:500}}>{c.name}</div>
                      <div style={{fontSize:11,color:"var(--ink3)"}}>{c.type}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div style={{textAlign:"center",marginTop:22}}>
            <div style={{fontSize:12,color:"var(--ink3)",marginBottom:10}}>— or —</div>
            <button style={S.btnPrimary} onClick={showAllCustomers} disabled={loading}>{loading?"Loading…":"Show all customers"}</button>
          </div>
        </div>
      )}
      {listLoaded&&(<>
      {singleCustomerId&&(
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,padding:"9px 14px",background:"var(--info-l)",borderRadius:8}}>
          <span style={{fontSize:13,color:"var(--info)"}}>Showing 1 customer: <strong>{customerQ}</strong></span>
          <div style={{display:"flex",gap:8}}>
            <button style={S.btnGhost} onClick={showAllCustomers}>Show all customers</button>
            <button style={S.btnGhost} onClick={resetToIdle}>✕ Clear</button>
          </div>
        </div>
      )}
      <div style={{display:"flex",gap:10,marginBottom:20,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{position:"relative",flex:1,maxWidth:300}}>
          <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--ink3)",fontSize:14,pointerEvents:"none"}}>🔍</span>
          <input placeholder="Search customer…" value={search} onChange={e=>setSearch(e.target.value)} style={{...S.input,paddingLeft:34,background:"#fff"}}/>
        </div>
        {[{key:"all",label:"All"},{key:"outstanding",label:"Outstanding"},{key:"settled",label:"Settled"}].map(f=>(
          <button key={f.key} onClick={()=>setFilter(f.key)}
            style={{padding:"6px 12px",borderRadius:20,fontSize:12,cursor:"pointer",border:"1px solid var(--paper3)",background:filter===f.key?"var(--accent)":"#fff",color:filter===f.key?"#fff":"var(--ink2)",fontFamily:"var(--mono)"}}>
            {f.label}
          </button>
        ))}
        <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--ink2)",cursor:"pointer",userSelect:"none"}}>
          <input type="checkbox" checked={showInvoiced} onChange={e=>setShowInvoiced(e.target.checked)} style={{width:13,height:13,cursor:"pointer",accentColor:"var(--accent)"}}/>
          Show invoiced &amp; outstanding details
        </label>
        <span style={{fontSize:12,color:"var(--ink3)",marginLeft:"auto"}}>{filtered.length} customer{filtered.length!==1?"s":""}</span>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:20,alignItems:"flex-end",flexWrap:"wrap"}}>
        <div>
          <label style={{...S.label,display:"block",marginBottom:5}}>From date</label>
          <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={S.input}/>
        </div>
        <div>
          <label style={{...S.label,display:"block",marginBottom:5}}>To date</label>
          <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} style={S.input}/>
        </div>
        <div style={{fontSize:12,color:"var(--ink3)",maxWidth:340,paddingBottom:8}}>
          {toDate ? "Outstanding as-of uses To date only. Period sales/receipts need both dates." : "Set a To date to see outstanding as of that date; set both dates for period activity."}
        </div>
        {(fromDate||toDate)&&<button style={{...S.btnSm,marginBottom:0}} onClick={()=>{setFromDate("");setToDate("");}}>✕ Clear dates</button>}
        {dateLoading&&<span style={{fontSize:12,color:"var(--ink3)",paddingBottom:8}}>Computing…</span>}
      </div>
      <div style={S.card}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
            <th style={S.th()}>Customer</th><th style={S.th()}>Type</th><th style={S.th(true)}>Total sales</th>
            {showInvoiced&&<th style={S.th(true)}>Total invoiced</th>}
            <th style={S.th(true)}>Total receipts</th>
            <th style={S.th(true)}>Balance</th>
            {showInvoiced&&<th style={S.th(true)}>Outstanding (invoiced)</th>}
            {showPeriod&&<th style={S.th(true)}>Period sales</th>}
            {showPeriod&&<th style={S.th(true)}>Period receipts</th>}
            {showAsOf&&<th style={S.th(true)}>Outstanding (as of {fmtDMY(toDate)})</th>}
           <th style={S.th()}>Status</th>
<th style={S.th()}></th>
          </tr></thead>
          <tbody>
            {loading&&<tr><td colSpan={totalCols} style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>Loading…</td></tr>}
            {!loading&&filtered.length===0&&<tr><td colSpan={totalCols}><div style={{textAlign:"center",padding:"60px 20px",color:"var(--ink3)"}}><div style={{fontSize:37,marginBottom:12,opacity:0.5}}>📊</div><p style={{fontSize:14}}>No data found.</p></div></td></tr>}
            {!loading&&filtered.map(r=>{
              const salesOut=parseFloat(r.total_sales||0)-parseFloat(r.total_receipts||0);
              const invOut=parseFloat(r.outstanding||0);
              return(
                <tr key={r.customer_id} style={{borderBottom:"1px solid var(--paper2)"}}>
                  <td style={{...S.td,fontWeight:500}}>{r.customer_name}</td>
                  <td style={S.td}>
                    <span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,background:r.customer_type==="outstation"?"var(--warn-l)":r.customer_type==="tpf"?"var(--info-l)":r.customer_type==="prv"?"#f3e8ff":"var(--accent-l)",color:r.customer_type==="outstation"?"var(--warn)":r.customer_type==="tpf"?"var(--info)":r.customer_type==="prv"?"#6a1b9a":"var(--accent)"}}>
                      {r.customer_type}
                    </span>
                  </td>
                  <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",color:"var(--info)",fontWeight:500}}>{fmt(r.total_sales)}</td>
                  {showInvoiced&&<td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",color:"#6a1b9a"}}>{fmt(r.total_invoiced)}</td>}
                  <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",color:"var(--accent)",fontWeight:500}}>{fmt(r.total_receipts)}</td>
                  <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:salesOut>0?"var(--danger)":"var(--accent)"}}>{fmt(salesOut)}</td>
                  {showInvoiced&&<td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:invOut>0?"var(--danger)":"var(--accent)"}}>{fmt(invOut)}</td>}
                  {showPeriod&&<td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",color:"var(--info)"}}>{fmt(r.period_sales)}</td>}
                  {showPeriod&&<td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",color:"var(--accent)"}}>{fmt(r.period_receipts)}</td>}
                  {showAsOf&&<td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:parseFloat(r.outstanding_as_of||0)>0?"var(--danger)":"var(--accent)"}}>{fmt(r.outstanding_as_of)}</td>}
                  <td style={S.td}>
                    {invOut<=0?<span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,background:"var(--accent-l)",color:"var(--accent)"}}>✓ Settled</span>
                    :parseFloat(r.total_invoiced)===0?<span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,background:"var(--paper2)",color:"var(--ink3)"}}>Not invoiced</span>
                    :<span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,background:"var(--danger-l)",color:"var(--danger)"}}>Outstanding</span>}
         </td>
                  <td style={S.td}>
                    <button style={S.btnSm} onClick={()=>downloadCustomerRecon(r)}>⬇</button>
                  </td>
                </tr>
              );
            })}
            {!loading&&filtered.length>0&&(
              <tr style={{background:"var(--paper2)",borderTop:"2px solid var(--paper3)"}}>
                <td style={{...S.td,fontWeight:500,color:"var(--ink3)"}} colSpan={2}>TOTAL ({filtered.length} customers)</td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:"var(--info)"}}>{fmt(totals.sales)}</td>
                {showInvoiced&&<td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:"#6a1b9a"}}>{fmt(totals.invoiced)}</td>}
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:"var(--accent)"}}>{fmt(totals.receipts)}</td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:totals.outstandingSales>0?"var(--danger)":"var(--accent)"}}>{fmt(totals.outstandingSales)}</td>
                {showInvoiced&&<td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:totals.outstanding>0?"var(--danger)":"var(--accent)"}}>{fmt(totals.outstanding)}</td>}
                {showPeriod&&<td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:"var(--info)"}}>{fmt(totals.periodSales)}</td>}
                {showPeriod&&<td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:"var(--accent)"}}>{fmt(totals.periodReceipts)}</td>}
                {showAsOf&&<td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:totals.asOfOutstanding>0?"var(--danger)":"var(--accent)"}}>{fmt(totals.asOfOutstanding)}</td>}
                <td></td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </>)}
    </div>
  );
}
// ── UNMATCHED CREDITS PAGE ────────────────────────────────────
export function UnmatchedCreditsPage({ toast }) {
  const [entries, setEntries]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modeFilter, setModeFilter] = useState("all");
  const [fromDate, setFromDate]   = useState("");
  const [toDate, setToDate]       = useState("");
  const [search, setSearch]       = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("bank_statement_entries")
      .select("*")
      .eq("entry_type", "credit")
      .is("matched_payment_id", null)
      .order("entry_date", { ascending: false });
    setEntries(data||[]); setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = entries.filter(e =>
    (modeFilter === "all" || e.payment_mode === modeFilter) &&
    (!fromDate || e.entry_date >= fromDate) &&
    (!toDate   || e.entry_date <= toDate) &&
    ((e.description||"").toLowerCase().includes(search.toLowerCase()) ||
     (e.raw_ref||"").toLowerCase().includes(search.toLowerCase()))
  );

  const totalUnmatched = filtered.reduce((s,e) => s + parseFloat(e.amount||0), 0);

  const downloadCSV = () => {
    const headers = ["Date","Bank","Amount","Description","Reference","Uploaded at"];
    const rows = filtered.map(e => [
      fmtDMY(e.entry_date),
      MODE_LABELS[e.payment_mode]||e.payment_mode,
      parseFloat(e.amount||0).toFixed(2),
      e.description||"",
      e.raw_ref||"",
      fmtDMY(e.uploaded_at)||"",
    ]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href=url; link.download=`unmatched-credits-${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  // Group by bank for summary
  const byBank = STATEMENT_MODES.map(m => ({
    mode: m,
    label: MODE_LABELS[m],
    count: entries.filter(e=>e.payment_mode===m).length,
    total: entries.filter(e=>e.payment_mode===m).reduce((s,e)=>s+parseFloat(e.amount||0),0),
  })).filter(b => b.count > 0);

  return (
    <div>
      <div style={S.pageHeader}>
        <div>
          <div style={S.pageTitle}>Unmatched Credits</div>
          <div style={S.subtitle}>Bank statement credits not yet matched to any recorded payment</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button style={S.btnGhost} onClick={downloadCSV}>⬇ CSV</button>
          <button style={S.btnGhost} onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {/* Bank summary cards */}
      {byBank.length > 0 && (
        <div style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(byBank.length,5)},1fr)`,gap:14,marginBottom:28}}>
          {byBank.map(b=>(
            <div key={b.mode}
              onClick={()=>setModeFilter(modeFilter===b.mode?"all":b.mode)}
              style={{...S.statCard(modeFilter===b.mode?"var(--accent)":"var(--warn)"),cursor:"pointer",outline:modeFilter===b.mode?"2px solid var(--accent)":"none"}}>
              <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:4}}>{b.label}</div>
              <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300,lineHeight:1,color:modeFilter===b.mode?"var(--accent)":"var(--ink)"}}>{b.count} entries</div>
              <div style={{fontFamily:"var(--mono)",fontSize:13,color:"var(--warn)",marginTop:4,fontWeight:500}}>{fmt(b.total)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Total unmatched */}
      {filtered.length > 0 && (
        <div style={{background:"var(--warn-l)",border:"1px solid var(--warn)",borderRadius:10,padding:"14px 20px",marginBottom:20,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontSize:13,color:"var(--warn)",fontWeight:500}}>
            ⚠ {filtered.length} unmatched credit{filtered.length!==1?"s":""} totalling {fmt(totalUnmatched)}
          </div>
          <div style={{fontSize:12,color:"var(--warn)"}}>These credits exist in your bank statement but have no matching recorded payment.</div>
        </div>
      )}

      {/* Filters */}
      <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{position:"relative",flex:1,maxWidth:280}}>
          <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--ink3)",fontSize:14,pointerEvents:"none"}}>🔍</span>
          <input placeholder="Search description or reference…" value={search} onChange={e=>setSearch(e.target.value)}
            style={{...S.input,paddingLeft:34,background:"#fff"}}/>
        </div>
        <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={{...S.input,width:"auto"}}/>
        <span style={{fontSize:12,color:"var(--ink3)"}}>to</span>
        <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} style={{...S.input,width:"auto"}}/>
        {["all",...STATEMENT_MODES].map(m=>(
          <button key={m} onClick={()=>setModeFilter(m)}
            style={{padding:"6px 12px",borderRadius:20,fontSize:12,cursor:"pointer",border:"1px solid var(--paper3)",background:modeFilter===m?"var(--accent)":"#fff",color:modeFilter===m?"#fff":"var(--ink2)",fontFamily:"var(--mono)"}}>
            {m==="all"?"All banks":MODE_LABELS[m]}
          </button>
        ))}
        {(search||fromDate||toDate||modeFilter!=="all")&&(
          <button style={S.btnSm} onClick={()=>{setSearch("");setFromDate("");setToDate("");setModeFilter("all");}}>✕ Clear</button>
        )}
        <span style={{fontSize:12,color:"var(--ink3)",marginLeft:"auto"}}>{filtered.length} entr{filtered.length!==1?"ies":"y"}</span>
      </div>

      {/* Table */}
      <div style={S.card}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
            <th style={S.th()}>Date</th>
            <th style={S.th()}>Bank</th>
            <th style={S.th()}>Description</th>
            <th style={S.th()}>Reference</th>
            <th style={S.th(true)}>Amount</th>
            <th style={S.th()}>Uploaded</th>
          </tr></thead>
          <tbody>
            {loading&&<tr><td colSpan={6} style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>Loading…</td></tr>}
            {!loading&&filtered.length===0&&(
              <tr><td colSpan={6}><div style={{textAlign:"center",padding:"60px 20px",color:"var(--ink3)"}}>
                <div style={{fontSize:37,marginBottom:12,opacity:0.5}}>✅</div>
                <p style={{fontSize:14,fontWeight:500,color:"var(--accent)"}}>All credits are matched!</p>
                <p style={{fontSize:12,marginTop:4}}>No unmatched bank statement credits found.</p>
              </div></td></tr>
            )}
            {!loading&&filtered.map((e,i)=>(
              <tr key={e.id||i} style={{borderBottom:"1px solid var(--paper2)"}}>
                <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12,fontWeight:500}}>{fmtDMY(e.entry_date)}</td>
                <td style={S.td}>
                  <span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,background:"var(--paper2)",color:"var(--ink2)"}}>
                    {MODE_LABELS[e.payment_mode]||e.payment_mode}
                  </span>
                </td>
                <td style={{...S.td,fontSize:12,maxWidth:260,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"var(--ink2)"}} title={e.description}>
                  {e.description||"—"}
                </td>
                <td style={{...S.td,fontFamily:"var(--mono)",fontSize:11,color:"var(--ink3)"}}>{e.raw_ref||"—"}</td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:"var(--accent)",fontSize:14}}>{fmt(e.amount)}</td>
                <td style={{...S.td,fontSize:11,color:"var(--ink3)"}}>{fmtDMY(e.uploaded_at)||"—"}</td>
              </tr>
            ))}
          </tbody>
          {!loading&&filtered.length>0&&(
            <tfoot>
              <tr style={{background:"var(--paper2)",borderTop:"2px solid var(--paper3)"}}>
                <td colSpan={4} style={{...S.td,fontWeight:500,color:"var(--ink3)"}}>TOTAL ({filtered.length} entries)</td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:"var(--warn)",fontSize:14}}>{fmt(totalUnmatched)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
